import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { formatMoney } from 'src/app/utils/commomService';
import {
  getListOrders,
  updateOrder,
  deleteOrder,
  resetChange,
  createOrder,
  updateOrderStatus,
  syncOrderDeviceTypes,
  updateOrderInList,
  uploadOrderImages,
  deleteOrderImage
} from 'src/features/orderSlice';
import {
  getOrderReceipts,
  exportOrderReceiptsExcel,
  updateTransactionInList
} from 'src/features/orderReceiptSlice';
import { getListSales, getListUsers } from 'src/features/userSlice';
import { getListStaff } from 'src/features/staffSlice';
import { getListDeviceType } from 'src/features/deviceTypeSlice';
import { getListDevice, updateDeviceTransfer } from 'src/features/deviceSlice';
import { getListAgencies } from 'src/features/agencySlice';
import {
  showGlobalToast,
  showDialogConfirm,
  closeDialogConfirm
} from 'src/features/uiSlice';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { messageToastType_const } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import OrderDialog from './OrderDialog';
import OrdersTable from './OrdersTable';
import ToolbarSection from './ToolbarSection';
import SerialDialog from './SerialDialog';
import GalleryModal from './GalleryModal';
import ConfirmDialogs from './ConfirmDialogs';
import InventoryTable from './InventoryTable';
import SalesDashboard from './SalesDashboard';
import InstallationsTable from './InstallationsTable';
import OrderDetailDialog from './OrderDetailDialog';
import PaymentReceiptsTable from './PaymentReceiptsTable';
import PaymentReceiptDialog from './PaymentReceiptDialog';
import WarrantyTicketsView from 'src/app/views/manage/warrantyTickets';
import {
  getListReceipts,
  createReceipt,
  updateReceipt,
  deleteReceipt
} from 'src/features/receiptSlice';
import { isAgencyCustomerOrder } from './orderUtils';

const FAKE_ORDERS = [];

const STATUS_CONFIG = {
  '-1': { label: 'Đã hủy', color: '#666', bg: '#f5f5f5' },
  0: { label: 'Chưa xử lý', color: '#e67e22', bg: '#fef9e7' },
  1: { label: 'Đang xử lý', color: '#2980b9', bg: '#eaf4fb' },
  2: { label: 'Đang vận chuyển', color: '#8e44ad', bg: '#f5eef8' },
  3: { label: 'Đã giao hàng', color: '#27ae60', bg: '#eafaf1' }
};


const PAGE_SIZE = 12;

// ─── Main Component ───────────────────────────────────────────────────────────
const SalesOrdersView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    isAdmin,
    isAccountant,
    isSale,
    isLeadMarketing,
    isAdminSale,
    isAgencySale,
    isVmakeSale,
    isCustomerCare,
    isAgency,
    isTechnician,
    agencyId,
    dataLogin,
    isGettingInfo
  } = useSelector(state => state.authSlice || {});
  const currentAgencyId = agencyId || dataLogin?.agency?.id || localStorage.getItem('agency-id');
  const token = localStorage.getItem('access-token');

  const location = useLocation();
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState(0);

  const isAnySale = Boolean(isSale || isLeadMarketing || isAdminSale || isAgencySale || isVmakeSale);
  const canManageReceipts = Boolean((isAdmin || isAccountant || isAgency) && !isAnySale);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      if (tabParam === 'warranty' || tabParam === 'maintenance' || tabParam === '3') {
        setMainTab(isCustomerCare ? 1 : 3);
        return;
      }
      if (tabParam === 'installation' || tabParam === '2') {
        setMainTab(isCustomerCare ? 0 : 2);
        return;
      }
      if (tabParam === 'receipts' || tabParam === '1') {
        if (!isAnySale) {
          setMainTab(0);
          setTransactionTypeFilter('RECEIPTS');
          return;
        }
      }
      if (tabParam === 'inventory' || tabParam === '4') {
        setMainTab(4);
        return;
      }
      if (tabParam === 'dashboard' || tabParam === '5') {
        setMainTab(5);
        return;
      }
      if (tabParam === 'sales' || tabParam === '0') {
        setMainTab(0);
        return;
      }
    } else if (isTechnician) {
      setMainTab(2);
    }
  }, [location.search, isCustomerCare, isTechnician, canManageReceipts, navigate, isAnySale]);

  const handleMainTabChange = (e, v) => {
    setMainTab(v);
    if (isCustomerCare) {
      const tabParam = v === 1 ? 'warranty' : 'installation';
      navigate(`/app/manage/sales-orders?tab=${tabParam}`, { replace: true });
    } else {
      const tabMap = {
        0: 'sales',
        2: 'installation',
        3: 'warranty',
        4: 'inventory',
        5: 'dashboard'
      };
      if (tabMap[v]) {
        navigate(`/app/manage/sales-orders?tab=${tabMap[v]}`, { replace: true });
      }
    }
  };
  const [tabForm, setTabForm] = useState(0);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState(isAnySale ? 'ORDERS' : 'ALL'); // 'ALL' | 'ORDERS' | 'RECEIPTS'
  const tabFilterBtnRefs = useRef({});
  const [tabIndicatorStyle, setTabIndicatorStyle] = useState({ left: 3, width: 0, opacity: 0 });

  useEffect(() => {
    if (isAnySale && transactionTypeFilter !== 'ORDERS') {
      setTransactionTypeFilter('ORDERS');
    }
  }, [isAnySale, transactionTypeFilter]);

  useEffect(() => {
    const updateIndicator = () => {
      const activeBtn = tabFilterBtnRefs.current[transactionTypeFilter];
      if (activeBtn) {
        setTabIndicatorStyle({
          left: activeBtn.offsetLeft,
          width: activeBtn.offsetWidth,
          opacity: 1
        });
      }
    };
    const timer = setTimeout(updateIndicator, 50);
    window.addEventListener('resize', updateIndicator);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [transactionTypeFilter, canManageReceipts]);

  const {
    transactions: unifiedTransactions = [],
    total: unifiedTotal = 0,
    summary: unifiedSummary = null,
    loading: unifiedLoading = false
  } = useSelector(state => state.orderReceiptSlice || {});

  const {
    listOrders,
    total,
    statusUpdate,
    statusDelete,
    statusCreate,
    totalAmountSum,
    totalPaidSum,
    amountBreakdown,
    statistic
  } = useSelector(state => state.orderSlice);
  const { listDeviceType } = useSelector(state => state.deviceTypeSlice);
  const { listAgencies } = useSelector(state => state.agencySlice || {});
  const { listUsers } = useSelector(state => state.userSlice || {});
  const { listSales } = useSelector(state => state.userSlice || {});
  const { listStaff = [] } = useSelector(state => state.staffSlice || {});

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterDevice, setFilterDevice] = useState('all');
  const [filterSale, setFilterSale] = useState('all');
  const [filterItemType, setFilterItemType] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [viewingSerials, setViewingSerials] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [viewDetailOrder, setViewDetailOrder] = useState(null);

  const handleOpenSerialModal = useCallback(order => {
    setEditingOrder(order);
    const initialDevices = (
      order.order_device_types ||
      order.devices ||
      []
    ).map(odt => ({
      ...JSON.parse(JSON.stringify(odt)),
      device_type_name:
        odt.device_type?.name ||
        odt.device_type_name ||
        odt.type ||
        listDeviceType.find(
          lt => lt.id === odt.device_type_id
        )?.name ||
        'N/A',
      device_ids:
        odt.order_details?.map(od => od.device_id).filter(Boolean) ||
        odt.device_ids ||
        [],
      serials:
        odt.order_details?.map(
          od =>
            od.serial ||
            od.device?.serial ||
            od.device?.imei
        ).filter(Boolean) ||
        odt.serials ||
        []
    }));
    setEditingDevices(initialDevices);
  }, [listDeviceType]);

  const handleViewDetailOrder = order => {
    setViewDetailOrder(order);
    setOpenDetailModal(true);
  };

  // Order Creation State
  const [openAddModal, setOpenAddModal] = useState(false);
  const [form, setForm] = useState({
    account: '',
    account_name: '',
    paymentMethod: 'Tiền mặt',
    type: '',
    simType: '',
    simNetwork: '',
    isNewOrder: '',
    note: '',
    fullName: '',
    phone: '',
    company: '',
    installationType: '',
    extra_fee: 0,
    vat: 0,
    order_type: 'SALE'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [orderForImage, setOrderForImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [devices, setDevices] = useState([
    { type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }
  ]);
  const [orderParts, setOrderParts] = useState([
    { product_id: '', product_code: '', product_name: '', quantity: 1, unit_price: 0, discount: 0, agency_id: '', description: '' }
  ]);
  const [fetchType, setFetchType] = useState('account');
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Payment Receipts (Phiếu Thu) Redux & API Integration
  const {
    listReceipts = [],
    totalAmountSum: receiptTotalAmountSum = 0,
    totalReceipts = 0,
    agencyOrderTotalAmount,
    agencyOrderCount
  } = useSelector(state => state.receiptSlice || {});

  const [receiptSearch, setReceiptSearch] = useState('');
  const [receiptSearchType, setReceiptSearchType] = useState('ALL');
  const [receiptFromDate, setReceiptFromDate] = useState('');
  const [receiptToDate, setReceiptToDate] = useState('');
  const [receiptAccountFilter, setReceiptAccountFilter] = useState('all');
  const [receiptAgencyFilter, setReceiptAgencyFilter] = useState('all');
  const [receiptPage, setReceiptPage] = useState(1);
  const [openReceiptModal, setOpenReceiptModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);

  const fetchListReceipts = useCallback(() => {
    const isRecTab = mainTab === 0 && transactionTypeFilter === 'RECEIPTS';

    const targetAgencyId = isAgency
      ? (currentAgencyId ? Number(currentAgencyId) : undefined)
      : (mainTab === 1 && receiptAgencyFilter !== 'all' ? Number(receiptAgencyFilter) : undefined);

    const params = {
      keywords: isRecTab ? (search || undefined) : (receiptSearch || undefined),
      account: (!isRecTab && receiptAccountFilter !== 'all') ? receiptAccountFilter : undefined,
      agency_id: targetAgencyId,
      from_date: isRecTab ? (fromDate || undefined) : (receiptFromDate || undefined),
      to_date: isRecTab ? (toDate || undefined) : (receiptToDate || undefined),
      page: isRecTab ? page : receiptPage,
      page_size: isRecTab ? PAGE_SIZE : 12
    };

    if (receiptSearchType === 'END_USER') {
      params.payer_type = 'END_USER';
    }

    dispatch(getListReceipts(params));
  }, [
    dispatch,
    mainTab,
    transactionTypeFilter,
    search,
    receiptSearch,
    receiptAccountFilter,
    receiptAgencyFilter,
    fromDate,
    receiptFromDate,
    toDate,
    receiptToDate,
    page,
    receiptPage,
    PAGE_SIZE,
    isAgency,
    currentAgencyId,
    receiptSearchType
  ]);

  useEffect(() => {
    if (mainTab === 1) {
      fetchListReceipts();
    }
  }, [fetchListReceipts, mainTab]);

  const flatAgencies = useMemo(() => {
    const flatten = arr => {
      let res = [];
      (arr || []).forEach(a => {
        if (!a) return;
        res.push(a);
        if (a.children && a.children.length > 0) {
          res.push(...flatten(a.children));
        }
      });
      return res;
    };
    return flatten(listAgencies || []);
  }, [listAgencies]);

  const agenciesMap = useMemo(() => {
    const byId = {};
    const byName = {};
    flatAgencies.forEach(ag => {
      if (!ag) return;
      if (ag.id) byId[String(ag.id)] = ag;
      const n1 = (ag.name || ag.fullname || '').trim().toLowerCase();
      if (n1) byName[n1] = ag;
      const n2 = (ag.username || ag.leader?.username || '').trim().toLowerCase();
      if (n2) byName[n2] = ag;
    });
    return { byId, byName };
  }, [flatAgencies]);

  const [agencyDebtMap, setAgencyDebtMap] = useState({});
  const [allOrdersCache, setAllOrdersCache] = useState([]);
  const [allReceiptsCache, setAllReceiptsCache] = useState([]);

  const fetchAgencyDebts = useCallback(async () => {
    if (isAnySale) return;
    try {
      const pageSize = 1000;
      const [orderRes, receiptRes] = await Promise.all([
        axiosInterceptor.get(`/order?page=1&page_size=${pageSize}`),
        axiosInterceptor.get(`/receipt?page=1&page_size=${pageSize}`)
      ]);

      const firstPayload = orderRes.data?.payload || {};
      const firstOrders = firstPayload.orders || orderRes.data?.orders || (Array.isArray(firstPayload) ? firstPayload : []);
      const totalOrders = orderRes.data?.numberOfItem || orderRes.data?.total || firstPayload.total || firstOrders.length;
      let combinedOrders = [...firstOrders];

      if (totalOrders > pageSize) {
        const totalPages = Math.ceil(totalOrders / pageSize);
        const promises = [];
        for (let p = 2; p <= Math.min(totalPages, 10); p++) {
          promises.push(axiosInterceptor.get(`/order?page=${p}&page_size=${pageSize}`));
        }
        const extraResponses = await Promise.all(promises);
        extraResponses.forEach(r => {
          const pData = r.data?.payload || {};
          const orders = pData.orders || r.data?.orders || (Array.isArray(pData) ? pData : []);
          combinedOrders = combinedOrders.concat(orders);
        });
      }

      const rPayload = receiptRes.data?.payload || {};
      const firstReceipts = rPayload.receipts || rPayload.data || (Array.isArray(rPayload) ? rPayload : []);
      const totalRecs = receiptRes.data?.numberOfItem ?? receiptRes.data?.total ?? firstReceipts.length;
      let combinedReceipts = [...firstReceipts];

      if (totalRecs > pageSize) {
        const totalRPages = Math.ceil(totalRecs / pageSize);
        const rPromises = [];
        for (let p = 2; p <= Math.min(totalRPages, 10); p++) {
          rPromises.push(axiosInterceptor.get(`/receipt?page=${p}&page_size=${pageSize}`));
        }
        const extraRResponses = await Promise.all(rPromises);
        extraRResponses.forEach(r => {
          const pData = r.data?.payload || {};
          const recs = pData.receipts || pData.data || (Array.isArray(pData) ? pData : []);
          combinedReceipts = combinedReceipts.concat(recs);
        });
      }

      const debtMap = {};

      (flatAgencies || []).forEach(ag => {
        if (!ag || !ag.id) return;
        const agId = String(ag.id);
        const agName = (ag.name || ag.fullname || '').trim();
        const agNameKey = agName.toLowerCase();
        const entry = {
          id: ag.id,
          name: agName,
          phone: ag.leader?.phone || ag.phone || '',
          address: ag.leader?.address || ag.address || '',
          orders: [],
          receipts: [],
          totalSales: 0,
          totalPaid: 0,
          debt: 0
        };
        debtMap[agId] = entry;
        if (agNameKey) debtMap[agNameKey] = entry;
      });

      combinedOrders.forEach(o => {
        if (o.status === -1) return;
        if (!isAgencyCustomerOrder(o)) return;

        const agId = String(o.agency_id || o.agency?.id || '');
        const agName = (o.agency?.name || o.customer_name || '').trim();
        const agNameKey = agName.toLowerCase();

        const grandTotal = Number(o.total_amount || o.totalAmount || 0);
        let entry = (agId && debtMap[agId]) || (agNameKey && debtMap[agNameKey]);
        if (!entry) {
          entry = { id: agId || null, name: agName, orders: [], receipts: [], totalSales: 0, totalPaid: 0, debt: 0 };
          if (agId) debtMap[agId] = entry;
          if (agNameKey) debtMap[agNameKey] = entry;
        }
        entry.orders.push(o);
        entry.totalSales += grandTotal;
      });

      combinedReceipts.forEach(r => {
        const pType = r.payer_type || r.customer_type;
        // Skip receipts belonging to end users or linked directly to retail orders
        if (pType === 'END_USER' || (r.order_id && pType !== 'AGENCY')) return;

        const agId = String(r.payer_id || (pType === 'AGENCY' ? (r.agency_id || r.agency?.id) : '') || '');
        const agName = (r.payer_name || (pType === 'AGENCY' ? (r.agency?.name || r.customer_name) : '') || '').trim();
        const agNameKey = agName.toLowerCase();
        const amount = Number(r.amount || 0);
        let entry = (agId && debtMap[agId]) || (agNameKey && debtMap[agNameKey]);
        if (!entry && pType === 'AGENCY') {
          entry = { id: agId || null, name: agName, orders: [], receipts: [], totalSales: 0, totalPaid: 0, debt: 0 };
          if (agId) debtMap[agId] = entry;
          if (agNameKey) debtMap[agNameKey] = entry;
        }
        if (entry) {
          entry.receipts.push(r);
          entry.totalPaid += amount;
        }
      });

      const seen = new Set();
      Object.values(debtMap).forEach(entry => {
        if (seen.has(entry)) return;
        seen.add(entry);
        entry.debt = Math.max(0, entry.totalSales - entry.totalPaid);
      });
      setAgencyDebtMap(debtMap);
      setAllOrdersCache(combinedOrders);
      setAllReceiptsCache(combinedReceipts);
    } catch (err) {
      console.error('Failed to compute agency debts:', err);
    }
  }, [flatAgencies, isAnySale]);

  useEffect(() => {
    if (!isAnySale) {
      fetchAgencyDebts();
    }
  }, [fetchAgencyDebts, isAnySale]);

  const handleOpenAddReceipt = (prefillData = null) => {
    setEditingReceipt(prefillData || null);
    setOpenReceiptModal(true);
  };

  const handleSaveReceipt = (data) => {
    if (editingReceipt && editingReceipt.id) {
      dispatch(updateReceipt({ id: editingReceipt.id, data })).then(res => {
        if (!res.error) {
          fetchListReceipts();
          fetchListOrders();
          fetchOrderReceipts();
          fetchAgencyDebts();
          setOpenReceiptModal(false);
          setEditingReceipt(null);
        }
      });
    } else {
      dispatch(createReceipt(data)).then(res => {
        if (!res.error) {
          fetchListReceipts();
          fetchListOrders();
          fetchOrderReceipts();
          fetchAgencyDebts();
          setOpenReceiptModal(false);
          setEditingReceipt(null);
        }
      });
    }
  };

  const [openDeleteReceiptConfirm, setOpenDeleteReceiptConfirm] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);

  const handleDeleteReceipt = (receipt) => {
    setReceiptToDelete(receipt);
    setOpenDeleteReceiptConfirm(true);
  };

  const confirmDeleteReceipt = () => {
    if (!receiptToDelete) return;
    dispatch(deleteReceipt(receiptToDelete.id)).then(res => {
      if (!res.error) {
        fetchListReceipts();
        fetchListOrders();
        fetchOrderReceipts();
        fetchAgencyDebts();
      }
      setOpenDeleteReceiptConfirm(false);
      setReceiptToDelete(null);
    });
  };

  // Serial Management State
  const [editingOrder, setEditingOrder] = useState(null);
  const [newSerial, setNewSerial] = useState('');
  const [editingDevices, setEditingDevices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Local state for tracking numbers to avoid excessive API calls
  const [localTrackingUrls, setLocalTrackingUrls] = useState({});
  const [countsByStatus, setCountsByStatus] = useState({});

  // Local state for extra fee (phí ship) inline editing
  const [localExtraFees, setLocalExtraFees] = useState({});

  // Dynamic Sticky Separator State
  const [isScrolled, setIsScrolled] = useState(false);

  // Horizontal Drag State
  const scrollContainerRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [openCompleteConfirm, setOpenCompleteConfirm] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);

  const [deletingImage, setDeletingImage] = useState(null);

  const handleDeleteImage = () => {
    if (!deletingImage) return;
    dispatch(deleteOrderImage({ id: deletingImage.id })).then(res => {
      if (!res.error) {
        const orderId = selectedGallery.orderId;
        const newImages = selectedGallery.images.filter(
          img => img.id !== deletingImage.id
        );
        dispatch(
          updateOrderInList({ id: orderId, data: { order_images: newImages } })
        );
        if (newImages.length === 0) {
          setSelectedGallery(null);
        } else {
          const nextIndex =
            selectedGallery.index >= newImages.length
              ? newImages.length - 1
              : selectedGallery.index;
          setSelectedGallery({
            ...selectedGallery,
            images: newImages,
            index: nextIndex
          });
        }
      }
      setDeletingImage(null);
    });
  };

  const searchTimeout = useRef(null);
  const originalDevicesRef = useRef([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOrder, setMenuOrder] = useState(null);

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setMenuOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuOrder(null);
  };

  const handleDeleteOrder = () => {
    dispatch(showDialogConfirm());
    setAnchorEl(null);
  };

  const handleCancelOrder = () => {
    setOpenCancelConfirm(true);
    setAnchorEl(null);
  };

  const confirmCancel = () => {
    if (!menuOrder) return;
    dispatch(
      updateOrderStatus({
        id: menuOrder.id,
        data: { status: -1, note: 'Hủy đơn hàng' },
        suffix: '/status'
      })
    ).then(() => {
      dispatch(updateOrderInList({ id: menuOrder.id, data: { status: -1 } }));
      fetchAgencyDebts();
      setOpenCancelConfirm(false);
      setMenuOrder(null);
    });
  };

  const confirmDelete = () => {
    if (menuOrder) {
      dispatch(deleteOrder({ id: menuOrder.id }));
      setMenuOrder(null);
      fetchAgencyDebts();
    }
    dispatch(closeDialogConfirm());
  };

  const onMouseDown = e => {
    setIsDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };
  const onMouseLeave = () => setIsDown(false);
  const onMouseUp = () => setIsDown(false);
  const onMouseMove = e => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    handleScroll();
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setIsScrolled(scrollContainerRef.current.scrollLeft > 0);
    }
  };

  const getPaymentStyle = method => {
    switch (method) {
      case 'Tiền mặt':
        return { color: '#27ae60', bg: '#eafaf1' };
      case 'Chuyển khoản':
        return { color: '#2980b9', bg: '#eaf4fb' };
      case 'Công nợ':
        return { color: '#e67e22', bg: '#fef9e7' };
      case 'COD':
        return { color: '#8e44ad', bg: '#f5eef8' };
      default:
        return { color: '#666', bg: '#f5f5f5' };
    }
  };

  const formatInputMoney = val =>
    String(val).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const parseInputMoney = val => {
    const num = Number(val.replace(/\D/g, ''));
    return num || 0;
  };

  useEffect(() => {
    if (statistic) {
      if (filterStatus === 'all' || !countsByStatus['-1']) {
        setCountsByStatus({
          '-1': statistic.total_cancelled_count || 0,
          0: statistic.total_pending_count || 0,
          1: statistic.total_processing_count || 0,
          2: statistic.total_shipping_count || 0,
          3: statistic.total_delivered_count || 0
        });
      }
    }
  }, [statistic, filterStatus]);

  const isUnifiedMode = !isAnySale && mainTab === 0 && transactionTypeFilter === 'ALL';

  const fetchOrderReceipts = useCallback(() => {
    const targetAgencyId = isAgency
      ? (currentAgencyId ? Number(currentAgencyId) : undefined)
      : undefined;

    dispatch(
      getOrderReceipts({
        page,
        page_size: PAGE_SIZE,
        keywords: search || undefined,
        agency_id: targetAgencyId,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        payment_status: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
        device_type_id: filterDevice !== 'all' ? Number(filterDevice) : undefined,
        sale_id: filterSale !== 'all' ? filterSale : undefined,
        type: 'both'
      })
    );
  }, [
    dispatch,
    page,
    search,
    fromDate,
    toDate,
    filterStatus,
    filterPaymentStatus,
    filterDevice,
    filterSale,
    isAgency,
    currentAgencyId
  ]);

  const fetchListOrders = useCallback(() => {
    dispatch(
      getListOrders({
        page,
        page_size: PAGE_SIZE,
        keywords: search || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        payment_status: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
        customer_type: isAgency ? 'AGENCY' : undefined,
        agency_id: isAgency ? currentAgencyId : undefined,
        device_type_id:
          filterDevice !== 'all' ? Number(filterDevice) : undefined,
        sale_id: filterSale !== 'all' ? filterSale : undefined,
        item_type: filterItemType !== 'all' ? filterItemType : undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined
      })
    );
  }, [
    dispatch,
    page,
    search,
    filterStatus,
    filterPaymentStatus,
    filterDevice,
    filterSale,
    filterItemType,
    fromDate,
    toDate,
    isAgency,
    currentAgencyId
  ]);

  useEffect(() => {
    if (!dataLogin || isCustomerCare || mainTab !== 0) return;
    if (isAnySale) {
      fetchListOrders();
      return;
    }
    if (transactionTypeFilter === 'ALL') {
      fetchOrderReceipts();
      fetchListOrders();
    } else if (transactionTypeFilter === 'RECEIPTS') {
      fetchListReceipts();
    } else {
      fetchListOrders();
    }
  }, [fetchListOrders, fetchOrderReceipts, fetchListReceipts, transactionTypeFilter, mainTab, isCustomerCare, dataLogin, isAnySale]);

  useEffect(() => {
    if (!listAgencies || listAgencies.length === 0) {
      dispatch(getListAgencies({ page: 1, page_size: 1000 }));
    }
  }, [dispatch, listAgencies]);

  useEffect(() => {
    if (openAddModal && tabForm === 0) {
      setSuggestionLoading(true);
      dispatch(
        getListAgencies({ page: 1, page_size: 1000 })
      ).finally(() => setSuggestionLoading(false));
    }
  }, [openAddModal, tabForm, dispatch]);

  const handleExportExcel = async () => {
    if (!isAnySale && isUnifiedMode) {
      const targetAgencyId = isAgency
        ? (currentAgencyId ? Number(currentAgencyId) : undefined)
        : undefined;

      dispatch(
        exportOrderReceiptsExcel({
          keywords: search || undefined,
          agency_id: targetAgencyId,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          payment_status: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
          device_type_id: filterDevice !== 'all' ? Number(filterDevice) : undefined,
          sale_id: filterSale !== 'all' ? filterSale : undefined,
          type: transactionTypeFilter === 'RECEIPTS' ? 'receipt' : 'both'
        })
      );
      return;
    }
    try {
      const queryObj = {};
      if (search) queryObj.keywords = search;
      if (filterStatus !== 'all') queryObj.status = filterStatus;
      if (filterPaymentStatus !== 'all') queryObj.payment_status = filterPaymentStatus;
      if (isAgency && currentAgencyId) {
        queryObj.customer_type = 'AGENCY';
        queryObj.agency_id = currentAgencyId;
      }
      if (filterDevice !== 'all') queryObj.device_type_id = Number(filterDevice);
      if (filterSale !== 'all') queryObj.sale_id = filterSale;
      if (filterItemType !== 'all') queryObj.item_type = filterItemType;
      if (fromDate) queryObj.from_date = fromDate;
      if (toDate) queryObj.to_date = toDate;

      const response = await axiosInterceptor.get(`/order/export/excel?${_convertObjectToQuery(queryObj)}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toLocaleDateString('vi-VN').replace(/\//g, '_');
      link.setAttribute('download', `danh_sach_don_hang_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export excel failed:', err);
      dispatch(
        showGlobalToast({
          message: 'Xuất file Excel thất bại!',
          type: messageToastType_const.error
        })
      );
    }
  };

  useEffect(() => {
    if (!dataLogin || isCustomerCare || (statusDelete !== 1 && statusCreate !== 1)) return;
    dispatch(getListOrders({ page, page_size: PAGE_SIZE }));
    dispatch(resetChange());
  }, [statusDelete, statusCreate, dispatch, page, isCustomerCare, dataLogin]);

  useEffect(() => {
    if (!dataLogin || isCustomerCare) return;
    dispatch(getListSales({ page: 1, page_size: 1000, role_id: 2 }));
    dispatch(getListStaff({ page: 1, page_size: 1000 }));
    dispatch(getListAgencies({ page: 1, page_size: 1000 }));
    dispatch(getListUsers({ page: 1, page_size: 1000 }));
  }, [dispatch, isCustomerCare, dataLogin]);

  const handleAddDevice = () => {
    const defaultSimType = tabForm === 0 ? 'Sim đại lý' : 'Sim VGPS';
    const firstType = (listDeviceType || [])[0]?.name || 'VGPS_CAM4G';
    setDevices([
      ...devices,
      { type: firstType, quantity: 1, unitPrice: 0, simType: defaultSimType, simNetwork: '' }
    ]);
  };
  const handleRemoveDevice = index => {
    const dev = devices[index];
    if (!isAdmin && dev?.hasDevicesAssigned) {
      dispatch(
        showGlobalToast({
          message:
            'Không thể xóa dòng thiết bị đã gán thiết bị! Vui lòng gỡ serial trước.',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    setDevices(devices.filter((_, i) => i !== index));
  };
  const handleDeviceChange = (index, field, value) => {
    const newDevices = [...devices];
    newDevices[index][field] = value;

    if (field === 'type') {
      const is2Cam = (value || '').toUpperCase().replace('-', '_').includes('2CAM');
      const currentNet = newDevices[index].simNetwork;
      if (is2Cam && ['Viettel', 'Vinaphone'].includes(currentNet)) {
        newDevices[index].simNetwork = '';
      } else if (!is2Cam && ['Sim ảnh', 'Sim live'].includes(currentNet)) {
        newDevices[index].simNetwork = '';
      }
    }

    if (field === 'simType' && value === 'Sim đại lý') {
      newDevices[index].simNetwork = '';
    }

    setDevices(newDevices);
  };

  const handleFetchUserInfo = selectedOption => {
    setSelectedUser(selectedOption);
    if (!selectedOption) {
      setForm(prev => ({
        ...prev,
        id: null,
        fullName: '',
        phone: '',
        company: '',
        account_name: ''
      }));
      return;
    }

    if (tabForm === 0) {
      const agency = selectedOption;
      setForm({
        ...form,
        id: agency.id || agency.agency_id || agency.agencyId,
        account:
          fetchType === 'account'
            ? agency.username || agency.account || agency.name || ''
            : agency.leader?.phone || agency.phone || agency.username || '',
        fullName: agency.name || agency.fullname || '',
        phone: agency.leader?.phone || agency.phone || '',
        company: agency.leader?.address || agency.address || '',
        account_name: agency.username || agency.account || agency.name || ''
      });
    } else {
      const user = selectedOption;
      setForm({
        ...form,
        id: user.id || user.user_id || user.userId,
        account: user.phone || user.username || user.account || '',
        fullName: user.full_name || user.fullname || user.username || '',
        phone: user.phone || '',
        company: user.agency?.name || user.address || '',
        account_name: user.username || user.account || ''
      });
    }
  };

  const handleAutocompleteSearch = keywords => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!keywords || keywords.trim().length === 0) {
      if (tabForm === 0) {
        setSuggestionLoading(true);
        dispatch(
          getListAgencies({ page: 1, page_size: 1000 })
        ).finally(() => setSuggestionLoading(false));
      } else {
        setSuggestionLoading(false);
      }
      return;
    }

    if (keywords.trim().length < 2) {
      setSuggestionLoading(false);
      return;
    }

    setSuggestionLoading(true);
    searchTimeout.current = setTimeout(() => {
      const searchAction = tabForm === 0 ? getListAgencies : getListUsers;
      dispatch(
        searchAction({ keywords, page: 1, page_size: 1000 })
      ).finally(() => setSuggestionLoading(false));
    }, 500);
  };

  const handleEditOrder = order => {
    setIsEditing(true);
    const targetOrderId = order.order_id || order.id;
    setEditOrderId(targetOrderId);
    const isAgency = order.customer_type === 'AGENCY';
    setTabForm(isAgency ? 0 : 1);

    const user = isAgency ? order.agency : order.end_user;
    setSelectedUser(user);
    setFetchType(isAgency ? 'account' : 'phone');

    const mapDevicesFromRaw = (rawList, fallbackSim) => {
      return (rawList || []).map(d => {
        const typeName =
          d.device_type?.name ||
          d.device_type_name ||
          d.type ||
          listDeviceType.find(lt => lt.id === (d.device_type_id || d.device_type?.id))?.name ||
          'N/A';
        const dSim = d.sim_method || d.simMethod || fallbackSim || order.sim_method || order.simMethod || '';
        const isSimVgps = dSim.includes('Sim VGPS') || dSim.includes('Sim Vgps');
        const networkMatch = dSim.match(/\(([^)]+)\)/);
        const network = networkMatch ? networkMatch[1] : '';
        const hasAssigned = Boolean(
          (d.order_details && d.order_details.some(od => od.device_id || od.device)) ||
          (d.device_ids && d.device_ids.length > 0) ||
          (d.serials && d.serials.length > 0)
        );
        return {
          id: (d.id !== undefined && d.id !== null && Number(d.id) > 0) ? Number(d.id) : undefined,
          device_type_id: d.device_type_id || d.device_type?.id,
          type: typeName,
          quantity: Number(d.quantity) || 1,
          unitPrice: d.unit_price !== undefined ? Number(d.unit_price) : (d.unitPrice !== undefined ? Number(d.unitPrice) : 0),
          simType: isSimVgps ? 'Sim VGPS' : (dSim.includes('Sim đại lý') ? 'Sim đại lý' : (isAgency ? 'Sim đại lý' : 'Sim VGPS')),
          simNetwork: network,
          hasDevicesAssigned: hasAssigned,
          order_details: d.order_details,
          serials: d.serials,
          device_ids: d.device_ids
        };
      });
    };

    const initialDevices = mapDevicesFromRaw(
      order.order_device_types || order.devices || [],
      order.sim_method || order.simMethod
    );
    originalDevicesRef.current = JSON.parse(JSON.stringify(initialDevices));

    const initialParts = (
      order.order_products ||
      order.products ||
      []
    ).map(p => ({
      id: p.id,
      product_id: p.product_id || p.productId || p.product?.id || '',
      product_code: p.product_code || p.product?.code || '',
      product_name: p.product_name || p.product?.name || '',
      quantity: p.quantity || 1,
      unit_price: p.unit_price || p.unitPrice || 0,
      discount: p.discount || 0,
      agency_id: p.agency_id || order.agency_id || '',
      description: p.description || ''
    }));

    let initialOrderType = order.order_type || 'SALE';
    if (initialParts.length > 0 && initialDevices.length > 0) {
      initialOrderType = 'BOTH';
    } else if (initialParts.length > 0) {
      initialOrderType = 'PART_SALE';
    } else if (initialDevices.length > 0) {
      initialOrderType = 'SALE';
    }

    setForm({
      id: isAgency ? order.agency_id : order.end_user_id,
      status: order.status,
      account: isAgency
        ? user?.username || user?.account || user?.name || ''
        : user?.phone || user?.username || user?.account || '',
      account_name: user?.username || user?.account || user?.name || '',
      fullName: user?.name || user?.fullname || user?.full_name || '',
      phone: user?.leader?.phone || user?.phone || '',
      company: user?.leader?.address || user?.address || '',
      paymentMethod: order.payment_method || 'Tiền mặt',
      type:
        order.active_method ||
        (isAgency ? 'VGPS kích hoạt' : 'Khách kích hoạt'),
      simType: order.sim_method
        ? order.sim_method.includes('Sim VGPS') || order.sim_method.includes('Sim Vgps')
          ? 'Sim VGPS'
          : order.sim_method
        : isAgency
          ? 'Sim đại lý'
          : 'Sim VGPS',
      simNetwork: order.sim_method?.includes('(')
        ? order.sim_method.match(/\(([^)]+)\)/)?.[1] || ''
        : '',
      isNewOrder: order.buy_method || 'Lắp mới ',
      note: order.description || '',
      installationType: (order.installer || 'Khách lắp đặt').replace(/Vgps/g, 'VGPS'),
      extra_fee: order.extra_fee || 0,
      vat: (order.vat !== undefined && order.vat !== null) ? order.vat : 0,
      order_type: initialOrderType
    });

    setDevices(
      initialDevices.length > 0
        ? initialDevices
        : [{ type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }]
    );

    setOrderParts(initialParts);

    // Fetch full order to ensure exact order_device_types IDs from database
    axiosInterceptor.get(`/order/${targetOrderId}`)
      .then(res => {
        const full = res.data?.payload?.order || res.data?.payload || res.data;
        if (full && full.id) {
          const serverDevList = full.order_device_types || full.devices || [];
          if (serverDevList.length > 0) {
            const mappedServerDevices = mapDevicesFromRaw(serverDevList, full.sim_method || full.simMethod);
            setDevices(mappedServerDevices);
            originalDevicesRef.current = JSON.parse(JSON.stringify(mappedServerDevices));
          }
          if (full.extra_fee !== undefined) {
            setForm(prev => ({ ...prev, extra_fee: full.extra_fee }));
          }
          if (full.vat !== undefined) {
            setForm(prev => ({ ...prev, vat: full.vat }));
          }
        }
      })
      .catch(err => {
        console.error('Fetch full order detail failed:', err);
      });

    axiosInterceptor.get(`/order/${targetOrderId}/products`)
      .then(res => {
        const parts = res.data?.payload || [];
        if (parts.length > 0) {
          const fetchedParts = parts.map(p => ({
            id: p.id,
            product_id: p.product_id,
            product_code: p.product_code || p.product?.code || '',
            product_name: p.product_name || p.product?.name || '',
            quantity: p.quantity || 1,
            unit_price: p.unit_price || 0,
            discount: p.discount || 0,
            agency_id: p.agency_id || order.agency_id || '',
            description: p.description || ''
          }));
          setOrderParts(fetchedParts);

          if (initialDevices.length > 0) {
            setForm(prev => ({ ...prev, order_type: 'BOTH' }));
          } else {
            setForm(prev => ({ ...prev, order_type: 'PART_SALE' }));
          }
        }
      })
      .catch(err => {
        console.error('Fetch order products failed:', err);
      });

    setOpenAddModal(true);
  };

  const handleCopyOrder = order => {
    setIsEditing(false);
    setEditOrderId(null);
    const isAgency = order.customer_type === 'AGENCY';
    setTabForm(isAgency ? 0 : 1);

    const user = isAgency ? order.agency : order.end_user;
    setSelectedUser(user);
    setFetchType(isAgency ? 'account' : 'phone');

    const initialDevices = (
      order.order_device_types ||
      order.devices ||
      []
    ).map(d => {
      const typeName =
        d.device_type?.name ||
        d.device_type_name ||
        d.type ||
        listDeviceType.find(lt => lt.id === d.device_type_id)?.name ||
        'N/A';
      const dSim = d.sim_method || d.simMethod || order.sim_method || order.simMethod || '';
      const isSimVgps = dSim.includes('Sim VGPS') || dSim.includes('Sim Vgps');
      const networkMatch = dSim.match(/\(([^)]+)\)/);
      const network = networkMatch ? networkMatch[1] : '';
      return {
        type: typeName,
        quantity: d.quantity,
        unitPrice: d.unit_price || d.unitPrice || 0,
        simType: isSimVgps ? 'Sim VGPS' : (dSim.includes('Sim đại lý') ? 'Sim đại lý' : (isAgency ? 'Sim đại lý' : 'Sim VGPS')),
        simNetwork: network
      };
    });

    const initialParts = (
      order.order_products ||
      order.products ||
      []
    ).map(p => ({
      product_id: p.product_id || p.productId || p.product?.id || '',
      product_code: p.product_code || p.product?.code || '',
      product_name: p.product_name || p.product?.name || '',
      quantity: p.quantity || 1,
      unit_price: p.unit_price || p.unitPrice || 0,
      discount: p.discount || 0,
      agency_id: p.agency_id || order.agency_id || '',
      description: p.description || ''
    }));

    let initialOrderType = order.order_type || 'SALE';
    if (initialParts.length > 0 && initialDevices.length > 0) {
      initialOrderType = 'BOTH';
    } else if (initialParts.length > 0) {
      initialOrderType = 'PART_SALE';
    } else if (initialDevices.length > 0) {
      initialOrderType = 'SALE';
    }

    const currentTracking = localTrackingUrls[order.id] !== undefined
      ? localTrackingUrls[order.id]
      : (order.tracking_number || '');

    setForm({
      id: isAgency ? order.agency_id : order.end_user_id,
      account: isAgency
        ? user?.username || user?.account || user?.name || ''
        : user?.phone || user?.username || user?.account || '',
      account_name: user?.username || user?.account || user?.name || '',
      fullName: user?.name || user?.fullname || user?.full_name || '',
      phone: user?.leader?.phone || user?.phone || '',
      company: user?.leader?.address || user?.address || '',
      paymentMethod: order.payment_method || 'Tiền mặt',
      type:
        order.active_method ||
        (isAgency ? 'VGPS kích hoạt' : 'Khách kích hoạt'),
      simType: order.sim_method
        ? order.sim_method.includes('Sim VGPS') || order.sim_method.includes('Sim Vgps')
          ? 'Sim VGPS'
          : order.sim_method
        : isAgency
          ? 'Sim đại lý'
          : 'Sim VGPS',
      simNetwork: order.sim_method?.includes('(')
        ? order.sim_method.match(/\(([^)]+)\)/)?.[1] || ''
        : '',
      isNewOrder: order.buy_method || 'Lắp mới ',
      note: order.description || '',
      installationType: (order.installer || 'Khách lắp đặt').replace(/Vgps/g, 'VGPS'),
      extra_fee: order.extra_fee || 0,
      vat: (order.vat !== undefined && order.vat !== null) ? order.vat : 0,
      order_type: initialOrderType,
      tracking_number: currentTracking
    });

    setDevices(
      initialDevices.length > 0
        ? initialDevices
        : [{ type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }]
    );

    setOrderParts(initialParts);

    axiosInterceptor.get(`/order/${order.id}/products`)
      .then(res => {
        const parts = res.data?.payload || [];
        if (parts.length > 0) {
          const fetchedParts = parts.map(p => ({
            product_id: p.product_id,
            product_code: p.product_code || p.product?.code || '',
            product_name: p.product_name || p.product?.name || '',
            quantity: p.quantity || 1,
            unit_price: p.unit_price || 0,
            discount: p.discount || 0,
            agency_id: p.agency_id || order.agency_id || '',
            description: p.description || ''
          }));
          setOrderParts(fetchedParts);

          if (initialDevices.length > 0) {
            setForm(prev => ({ ...prev, order_type: 'BOTH' }));
          } else {
            setForm(prev => ({ ...prev, order_type: 'PART_SALE' }));
          }
        }
      })
      .catch(err => {
        console.error('Fetch order products failed:', err);
      });

    setOpenAddModal(true);
  };

  const handleCreateOrder = () => {
    if (submitting) return;

    if (!form.account || !form.id) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng nhập và kiểm tra thông tin khách hàng!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    const isSale = form.order_type !== 'PART_SALE';

    if (
      isSale && (
        !form.type ||
        !form.installationType ||
        !form.isNewOrder
      )
    ) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng chọn đầy đủ các thông tin bắt buộc!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    const hasDevice = form.order_type === 'SALE' || form.order_type === 'BOTH';
    const hasPart = form.order_type === 'PART_SALE' || form.order_type === 'BOTH';

    if (hasDevice && (!devices || devices.length === 0)) {
      if (!hasPart || !orderParts || orderParts.length === 0) {
        dispatch(
          showGlobalToast({
            message: 'Vui lòng thêm ít nhất một thiết bị hoặc phụ kiện vào đơn hàng!',
            type: messageToastType_const.warning
          })
        );
        return;
      }
    }

    if (hasDevice && devices && devices.length > 0) {
      const hasEmptyDevice = devices.some(d => !d.type);
      if (hasEmptyDevice) {
        dispatch(
          showGlobalToast({
            message: 'Vui lòng chọn đầy đủ thông tin thiết bị hoặc xóa dòng trống!',
            type: messageToastType_const.warning
          })
        );
        return;
      }

      for (const d of devices) {
        const effectiveSimType = tabForm === 1 ? 'Sim VGPS' : (d.simType || 'Sim đại lý');
        if (effectiveSimType === 'Sim VGPS' && !d.simNetwork) {
          const is2Cam = (d.type || '').toUpperCase().replace('-', '_').includes('2CAM');
          dispatch(
            showGlobalToast({
              message: is2Cam
                ? `Vui lòng chọn loại SIM (Sim ảnh hoặc Sim live) cho thiết bị ${d.type}!`
                : `Vui lòng chọn nhà mạng cho Sim VGPS của thiết bị ${d.type}!`,
              type: messageToastType_const.warning
            })
          );
          return;
        }
      }
    }

    if (hasPart) {
      const hasEmptyPart = orderParts.some(op => !op.product_id);
      if (hasEmptyPart) {
        dispatch(
          showGlobalToast({
            message: 'Vui lòng chọn đầy đủ thông tin phụ kiện hoặc xóa dòng trống!',
            type: messageToastType_const.warning
          })
        );
        return;
      }
    }

    const getDeviceSimStr = d => {
      const sType = tabForm === 1 ? 'Sim VGPS' : (d.simType || 'Sim đại lý');
      if (sType === 'Sim VGPS') {
        return d.simNetwork ? `Sim VGPS (${d.simNetwork})` : 'Sim VGPS';
      }
      return sType;
    };

    const simSummary = form.order_type === 'PART_SALE'
      ? 'Không dùng sim'
      : (devices.length === 1
        ? getDeviceSimStr(devices[0])
        : devices.map(d => `${d.quantity}x ${d.type}: ${getDeviceSimStr(d)}`).join('; ')
      );

    const vatRate = Number(form.vat) || 0;
    const deviceSum = devices.reduce((acc, d) => acc + d.quantity * (d.unitPrice || 0), 0);
    const partSum = orderParts.reduce((acc, p) => acc + (p.quantity || 0) * (p.unit_price || p.unitPrice || 0) - (p.discount || 0), 0);

    const base_amount = form.order_type === 'BOTH'
      ? (deviceSum + partSum)
      : (form.order_type === 'PART_SALE' ? partSum : deviceSum);
    const discount_amount = Number(form.discount || 0);
    const taxable_base = Math.max(0, base_amount - discount_amount);
    const vat_amount = taxable_base * (vatRate / 100);
    const extra_fee = Number(form.extra_fee) || 0;
    // Backend automatically adds extra_fee to total_amount in DB: total_amount (FE sent) = (base - discount) * (1 + vat/100)
    const total_amount = taxable_base + vat_amount;

    const tracking_number_val = (form.order_type !== 'PART_SALE' && form.installationType === 'VGPS lắp đặt')
      ? 'offline'
      : (isEditing ? (form.tracking_number || undefined) : '');

    if (!isAdmin && isEditing && originalDevicesRef.current && originalDevicesRef.current.length > 0) {
      for (const orig of originalDevicesRef.current) {
        if (orig.id && orig.hasDevicesAssigned) {
          const current = devices.find(d => d.id === orig.id);
          if (!current || Number(current.quantity) === 0) {
            dispatch(
              showGlobalToast({
                message: 'Không thể xóa dòng thiết bị đã gán thiết bị/serial! Vui lòng gỡ serial trước.',
                type: messageToastType_const.warning
              })
            );
            return;
          }
          const assignedCount =
            (orig.order_details || []).filter(od => od.device_id || od.device).length ||
            (orig.device_ids || []).length ||
            (orig.serials || []).length ||
            0;
          if (assignedCount > 0 && Number(current.quantity) < assignedCount) {
            dispatch(
              showGlobalToast({
                message: `Dòng thiết bị ${orig.type} đã gán ${assignedCount} thiết bị, không thể giảm số lượng nhỏ hơn ${assignedCount}!`,
                type: messageToastType_const.warning
              })
            );
            return;
          }
        }
      }
    }

    const checkDevicesChanged = () => {
      if (!isEditing) return true;
      const origList = originalDevicesRef.current || [];
      const currList = (devices || []).filter(d => d.type);
      if (origList.length !== currList.length) return true;
      for (let i = 0; i < currList.length; i++) {
        const c = currList[i];
        const o = origList[i];
        if (!o) return true;
        if (c.id !== o.id) return true;
        if (c.type !== o.type) return true;
        if (Number(c.quantity) !== Number(o.quantity)) return true;
        if (Number(c.unitPrice) !== Number(o.unitPrice)) return true;
        if (getDeviceSimStr(c) !== getDeviceSimStr(o)) return true;
      }
      return false;
    };
    const isDevChanged = checkDevicesChanged();

    let order_device_types_payload = undefined;
    if (!isEditing || isDevChanged) {
      if (form.order_type === 'PART_SALE' || !devices) {
        order_device_types_payload = [];
      } else {
        order_device_types_payload = devices.filter(d => d.type).map(d => {
          const item = {
            quantity: Number(d.quantity),
            unit_price: Number(d.unitPrice) || 0,
            status: 0,
            sim_method: getDeviceSimStr(d)
          };
          if (isEditing && d.id && Number(d.id) > 0) {
            item.id = Number(d.id);
          } else {
            item.device_type_id = listDeviceType.find(lt => lt.name === d.type)?.id || 1;
          }
          return item;
        });
      }
    }

    const orderData = {
      description: form.note || `Đơn cho: ${form.fullName || form.account}`,
      shipping_unit: '',
      order_type: form.order_type === 'BOTH' ? 'SALE' : (form.order_type || 'SALE'),
      customer_type: tabForm === 0 ? 'AGENCY' : 'END_USER',
      status: isEditing ? undefined : 0,
      total_amount: total_amount,
      extra_fee: extra_fee,
      paid: isEditing ? undefined : 0,
      vat: vatRate,
      code: isEditing ? undefined : '',
      end_user_id: tabForm === 1 ? form.id : null,
      agency_id: tabForm === 0 ? form.id : null,
      parent_id: null,
      payment_method: form.paymentMethod,
      active_method: form.order_type === 'PART_SALE' ? 'Khách tự kích hoạt' : (form.active_method || form.type),
      sim_method: simSummary,
      buy_method: form.order_type === 'PART_SALE' ? 'Bán phụ kiện' : form.isNewOrder,
      installer: form.order_type === 'PART_SALE' ? 'Khách tự lắp' : form.installationType,
      delivery_method: (form.order_type !== 'PART_SALE' && form.installationType === 'VGPS lắp đặt') ? 0 : 1,
      tracking_number: tracking_number_val,
      ...(order_device_types_payload !== undefined ? { order_device_types: order_device_types_payload } : {}),
      order_products: (form.order_type === 'SALE' || !orderParts) ? [] : orderParts.filter(op => op.product_id).map(op => ({
        id: op.id || undefined,
        product_id: Number(op.product_id),
        quantity: Number(op.quantity),
        unit_price: Number(op.unit_price) || 0,
        discount: Number(op.discount) || 0,
        description: op.description || ''
      })),
      ...(form.sale_id ? { sale_id: form.sale_id } : {})
    };

    setSubmitting(true);
    if (isEditing) {
      dispatch(updateOrder({ id: editOrderId, data: orderData })).then(res => {
        setSubmitting(false);
        if (res.meta.requestStatus === 'fulfilled') {
          const updatedOrder = res.payload?.payload?.order || res.payload?.payload || res.payload?.order || res.payload;
          if (editOrderId && tracking_number_val !== undefined) {
            setLocalTrackingUrls(prev => ({
              ...prev,
              [editOrderId]: tracking_number_val
            }));
          }
          if (updatedOrder && updatedOrder.id) {
            dispatch(updateOrderInList({ id: editOrderId, data: updatedOrder }));
            dispatch(updateTransactionInList({ id: editOrderId, code: updatedOrder.code, data: updatedOrder }));
            setAllOrdersCache(prev =>
              (prev || []).map(o => (o.id === editOrderId || o.order_id === editOrderId ? { ...o, ...updatedOrder } : o))
            );
          }
          setOpenAddModal(false);
          setIsEditing(false);
          setEditOrderId(null);
          setForm({
            account: '',
            paymentMethod: 'Tiền mặt',
            type: tabForm === 0 ? 'VGPS kích hoạt' : 'Khách kích hoạt',
            simType: '',
            simNetwork: '',
            note: '',
            fullName: '',
            phone: '',
            company: '',
            installationType: 'Khách lắp đặt',
            extra_fee: 0,
            vat: 0,
            order_type: 'SALE',
            tracking_number: ''
          });
          setDevices([{ type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }]);
          setOrderParts([
            { product_id: '', product_code: '', product_name: '', quantity: 1, unit_price: 0, discount: 0, agency_id: '', description: '' }
          ]);
          originalDevicesRef.current = [];

          if (transactionTypeFilter === 'ALL') {
            fetchOrderReceipts();
            fetchListOrders();
          } else {
            fetchListOrders();
            if (fetchOrderReceipts) fetchOrderReceipts();
          }
        } else {
          const errorMsg = res.payload?.message || res.error?.message || '';
          if (errorMsg.includes('already has devices assigned') || errorMsg.includes('đã gán thiết bị')) {
            dispatch(
              showGlobalToast({
                message: 'Không thể xóa dòng thiết bị đã gán thiết bị/serial! Vui lòng gỡ serial trước.',
                type: messageToastType_const.error
              })
            );
          }
        }
      }).catch(err => {
        setSubmitting(false);
        const errorMsg = err?.data?.message || err?.message || '';
        if (errorMsg.includes('already has devices assigned') || errorMsg.includes('đã gán thiết bị')) {
          dispatch(
            showGlobalToast({
              message: 'Không thể xóa dòng thiết bị đã gán thiết bị/serial! Vui lòng gỡ serial trước.',
              type: messageToastType_const.error
            })
          );
        }
      });
    } else {
      dispatch(createOrder(orderData)).then(res => {
        setSubmitting(false);
        if (res.meta.requestStatus === 'fulfilled') {
          setOpenAddModal(false);
          setForm({
            account: '',
            paymentMethod: 'Tiền mặt',
            type: tabForm === 0 ? 'VGPS kích hoạt' : 'Khách kích hoạt',
            simType: '',
            simNetwork: '',
            note: '',
            fullName: '',
            phone: '',
            company: '',
            installationType: 'Khách lắp đặt',
            extra_fee: 0,
            vat: 0,
            order_type: 'SALE'
          });
          setDevices([{ type: 'VGPS_CAM4G', quantity: 1, unitPrice: 0 }]);
          setOrderParts([
            { product_id: '', product_code: '', product_name: '', quantity: 1, unit_price: 0, discount: 0, agency_id: '', description: '' }
          ]);
        }
      }).catch(() => setSubmitting(false));
    }
  };
  const handleDeliveredToggle = (order, checked) => {
    const isPartSale =
      order.buy_method === 'Bán phụ kiện' ||
      order.buyMethod === 'Bán phụ kiện' ||
      order.order_type === 'PART_SALE' ||
      order.orderType === 'PART_SALE';

    if (
      checked &&
      !isPartSale &&
      order.status < 2 &&
      order.delivery_method !== 0 &&
      order.installer?.replace(/Vgps/g, 'VGPS') !== 'VGPS lắp đặt'
    ) {
      dispatch(
        showGlobalToast({
          message:
            'Vui lòng nhập link vận đơn trước khi xác nhận đã giao hàng!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    const targetOrderId = order.id || order.order_id;
    const newStatus = checked ? 3 : 2;
    dispatch(
      updateOrderStatus({
        id: targetOrderId,
        data: {
          status: newStatus,
          note: checked ? 'Đã giao hàng' : 'Đang vận chuyển'
        },
        suffix: '/status'
      })
    ).then(res => {
      if (res && res.meta && res.meta.requestStatus === 'rejected') return;
      dispatch(
        updateOrderInList({ id: targetOrderId, data: { status: newStatus } })
      );
      dispatch(
        updateTransactionInList({
          id: targetOrderId,
          code: order.code || order.order_code,
          data: { status: newStatus }
        })
      );
      setAllOrdersCache(prev =>
        (prev || []).map(o => (o.id === targetOrderId || o.order_id === targetOrderId ? { ...o, status: newStatus } : o))
      );
      if (transactionTypeFilter === 'ALL') {
        fetchOrderReceipts();
      } else {
        fetchListOrders();
      }
    });
  };

  const handleCompleteOrder = order => {
    setOpenCompleteConfirm(true);
    setAnchorEl(null);
  };

  const confirmComplete = () => {
    if (!menuOrder) return;
    const order = menuOrder;
    const targetOrderId = order.id || order.order_id;
    Promise.all([
      dispatch(
        updateOrderStatus({
          id: targetOrderId,
          data: { status: 4, note: 'Hoàn thành đơn hàng' },
          suffix: '/status'
        })
      ),
      dispatch(
        updateOrder({
          id: targetOrderId,
          data: { paid: 1 }
        })
      )
    ]).then(() => {
      dispatch(
        updateOrderInList({ id: targetOrderId, data: { status: 4, paid: 1 } })
      );
      dispatch(
        updateTransactionInList({
          id: targetOrderId,
          code: order.code || order.order_code,
          data: { status: 4, paid: 1 }
        })
      );
      setAllOrdersCache(prev =>
        (prev || []).map(o => (o.id === targetOrderId || o.order_id === targetOrderId ? { ...o, status: 4, paid: 1 } : o))
      );
      if (transactionTypeFilter === 'ALL') {
        fetchOrderReceipts();
      } else {
        fetchListOrders();
      }
      setOpenCompleteConfirm(false);
      setMenuOrder(null);
    });
  };

  const handleSaveExtraFee = order => {
    const raw = localExtraFees[order.id];
    if (raw === undefined) return;
    const newFee = Number(String(raw).replace(/\D/g, '')) || 0;
    const oldFee = order.extra_fee || 0;
    if (newFee === oldFee) return;

    const deviceList = order.order_device_types || order.devices || [];
    const productList = order.order_products || order.products || [];
    const dSub = deviceList.reduce(
      (sum, d) => sum + (d.quantity || 0) * (d.unit_price || d.unitPrice || 0),
      0
    );
    const pSub = productList.reduce(
      (sum, p) => sum + (p.quantity || 0) * (p.unit_price || p.unitPrice || 0) - (p.discount || 0),
      0
    );
    const base = dSub + pSub;
    const discount = Number(order.discount || 0);
    const taxableBase = Math.max(0, base - discount);
    const vatRate = Number(order.vat || 0);
    const totalAmountToSend = taxableBase * (1 + vatRate / 100);

    dispatch(
      updateOrder({
        id: order.id,
        data: { extra_fee: newFee, total_amount: totalAmountToSend }
      })
    ).then(() => {
      if (transactionTypeFilter === 'ALL') {
        fetchOrderReceipts();
        fetchListOrders();
      } else {
        fetchListOrders();
      }
    });
  };

  const handleSaveTrackingUrl = order => {
    const url = localTrackingUrls[order.id];
    if (url === undefined || url === (order.tracking_number || '')) return;
    const targetOrderId = order.id || order.order_id;

    if (url) {
      const newStatus = (order.status || 0) < 2 ? 2 : order.status;
      Promise.all([
        dispatch(
          updateOrderStatus({
            id: targetOrderId,
            data: { status: newStatus, note: 'Đang vận chuyển' },
            suffix: '/status'
          })
        ),
        dispatch(updateOrder({ id: targetOrderId, data: { tracking_number: url } }))
      ]).then(() => {
        dispatch(
          updateOrderInList({
            id: targetOrderId,
            data: { status: newStatus, tracking_number: url }
          })
        );
        dispatch(
          updateTransactionInList({
            id: targetOrderId,
            code: order.code || order.order_code,
            data: { status: newStatus, tracking_number: url }
          })
        );
        setAllOrdersCache(prev =>
          (prev || []).map(o => (o.id === targetOrderId || o.order_id === targetOrderId ? { ...o, status: newStatus, tracking_number: url } : o))
        );
        if (transactionTypeFilter === 'ALL') {
          fetchOrderReceipts();
        } else {
          fetchListOrders();
        }
      });
    } else {
      Promise.all([
        dispatch(
          updateOrderStatus({
            id: targetOrderId,
            data: { status: 1, note: 'Đang xử lý' },
            suffix: '/status'
          })
        ),
        dispatch(updateOrder({ id: targetOrderId, data: { tracking_number: '' } }))
      ]).then(() => {
        dispatch(
          updateOrderInList({
            id: targetOrderId,
            data: { status: 1, tracking_number: '' }
          })
        );
        dispatch(
          updateTransactionInList({
            id: targetOrderId,
            code: order.code || order.order_code,
            data: { status: 1, tracking_number: '' }
          })
        );
        setAllOrdersCache(prev =>
          (prev || []).map(o => (o.id === targetOrderId || o.order_id === targetOrderId ? { ...o, status: 1, tracking_number: '' } : o))
        );
        if (transactionTypeFilter === 'ALL') {
          fetchOrderReceipts();
        } else {
          fetchListOrders();
        }
      });
    }
  };

  const handleBypassSerial = order => {
    if (!order || !isAdmin) return;
    const targetOrderId = order.id || order.order_id;
    const isOffline =
      order.delivery_method === 0 ||
      order.installer?.replace(/Vgps/g, 'VGPS') === 'VGPS lắp đặt';
    const targetStatus = isOffline ? 2 : 1;
    const targetNote = isOffline ? 'Sẵn sàng giao (Offline)' : 'Đang xử lý';

    dispatch(
      updateOrderStatus({
        id: targetOrderId,
        data: { status: targetStatus, note: targetNote },
        suffix: '/status'
      })
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(
          updateOrderInList({
            id: targetOrderId,
            data: { status: targetStatus }
          })
        );
        dispatch(
          updateTransactionInList({
            id: targetOrderId,
            code: order.code || order.order_code,
            data: { status: targetStatus }
          })
        );
        setAllOrdersCache(prev =>
          (prev || []).map(o => (o.id === targetOrderId || o.order_id === targetOrderId ? { ...o, status: targetStatus } : o))
        );
        dispatch(
          showGlobalToast({
            message: `Đã chuyển đơn sang trạng thái ${STATUS_CONFIG[targetStatus]?.label || 'Đang xử lý'} (Bỏ qua Serial)!`,
            type: messageToastType_const.success
          })
        );
        if (transactionTypeFilter === 'ALL') {
          fetchOrderReceipts();
        } else {
          fetchListOrders();
        }
      }
    });
  };

  const ordersLookupMap = useMemo(() => {
    const byId = {};
    const byCode = {};
    const registerOrder = o => {
      if (!o) return;
      if (o.id) byId[String(o.id)] = o;
      if (o.order_id) byId[String(o.order_id)] = o;
      if (o.code) byCode[String(o.code).trim().toLowerCase()] = o;
      if (o.order_code) byCode[String(o.order_code).trim().toLowerCase()] = o;
      if (o.orderCode) byCode[String(o.orderCode).trim().toLowerCase()] = o;
    };
    (allOrdersCache || []).forEach(registerOrder);
    (listOrders || []).forEach(registerOrder);
    return { byId, byCode };
  }, [allOrdersCache, listOrders]);

  const receiptsLookupMap = useMemo(() => {
    const byId = {};
    const byCode = {};
    const registerReceipt = r => {
      if (!r) return;
      if (r.id) byId[String(r.id)] = r;
      if (r.code) byCode[String(r.code).trim().toLowerCase()] = r;
    };
    (allReceiptsCache || []).forEach(registerReceipt);
    (listReceipts || []).forEach(registerReceipt);
    return { byId, byCode };
  }, [allReceiptsCache, listReceipts]);

  // Pre-fetch any orders in unifiedTransactions that are not yet in ordersLookupMap
  useEffect(() => {
    if (!isUnifiedMode || !Array.isArray(unifiedTransactions) || unifiedTransactions.length === 0) return;

    const missingOrderIds = unifiedTransactions
      .filter(t => t.type !== 'receipt')
      .map(t => t.order_id || t.id)
      .filter(id => id && !ordersLookupMap.byId[String(id)]);

    if (missingOrderIds.length > 0) {
      Promise.all(
        missingOrderIds.slice(0, 20).map(id =>
          axiosInterceptor
            .get(`/order/${id}`)
            .then(res => res.data?.payload || res.data)
            .catch(() => null)
        )
      ).then(results => {
        const validOrders = results.filter(o => o && o.id);
        if (validOrders.length > 0) {
          setAllOrdersCache(prev => {
            const existingIds = new Set((prev || []).map(o => String(o.id)));
            const newOrders = validOrders.filter(o => !existingIds.has(String(o.id)));
            return newOrders.length > 0 ? [...prev, ...newOrders] : prev;
          });
        }
      });
    }
  }, [unifiedTransactions, isUnifiedMode, ordersLookupMap]);

  const usersLookupMap = useMemo(() => {
    const byId = {};
    const byName = {};
    const byUsername = {};

    const registerUser = (u, defaultRole = '') => {
      if (!u) return;
      const uid = u.id ? String(u.id) : null;
      const fn = (u.full_name || u.name || '').trim().toLowerCase();
      const un = (u.username || '').trim().toLowerCase();

      const userRole = u.role?.title || (typeof u.role === 'string' ? u.role : '') || u.role_name || defaultRole;
      const roleId = Number(u.role_id || u.roleId || u.role?.id || (defaultRole === 'Sale' ? 2 : 0));

      const entry = {
        ...u,
        role_id: roleId,
        role_name: userRole,
        role: u.role || (userRole ? { id: roleId, title: userRole } : undefined)
      };

      if (uid) byId[uid] = entry;
      if (fn) byName[fn] = entry;
      if (un) byUsername[un] = entry;
    };

    // ONLY staff and sales, NEVER customers (listUsers)
    (listSales || []).forEach(u => registerUser(u, 'Sale'));
    (listStaff || []).forEach(u => registerUser(u, ''));
    if (dataLogin) registerUser(dataLogin, '');

    return { byId, byName, byUsername };
  }, [listSales, listStaff, dataLogin]);

  const getUserRole = useCallback((userOrIdOrName, fallback = '') => {
    if (!userOrIdOrName) return fallback || '';
    let u = null;
    if (typeof userOrIdOrName === 'object') {
      if (userOrIdOrName.role?.title || userOrIdOrName.role_name || (typeof userOrIdOrName.role === 'string' && userOrIdOrName.role)) {
        const rawRole = String(userOrIdOrName.role?.title || userOrIdOrName.role_name || userOrIdOrName.role).toLowerCase();
        if (rawRole.includes('admin') || rawRole.includes('quản trị')) return 'Quản trị';
        if (rawRole.includes('sale') || rawRole.includes('bán hàng')) return 'Sale';
        if (rawRole.includes('cskh') || rawRole.includes('customer_care') || rawRole.includes('chăm sóc')) return 'CSKH';
        if (rawRole.includes('kế toán') || rawRole.includes('accountant')) return 'Kế toán';
        if (rawRole.includes('kỹ thuật') || rawRole.includes('tech') || rawRole.includes('technician')) return 'Kỹ thuật';
        if (rawRole.includes('lead') || rawRole.includes('marketing')) return 'Lead Marketing';
        if (rawRole.includes('đại lý') || rawRole.includes('agency')) return 'Đại lý';
        if (rawRole.includes('enduser') || rawRole.includes('customer')) return 'Sale';
        return userOrIdOrName.role?.title || userOrIdOrName.role_name || userOrIdOrName.role;
      }
      const uid = userOrIdOrName.id;
      const uname = (userOrIdOrName.full_name || userOrIdOrName.name || '').trim().toLowerCase();
      const uuser = (userOrIdOrName.username || '').trim().toLowerCase();
      u = (uid && usersLookupMap.byId[String(uid)]) ||
          (uname && usersLookupMap.byName[uname]) ||
          (uuser && usersLookupMap.byUsername[uuser]) ||
          userOrIdOrName;
    } else if (typeof userOrIdOrName === 'number') {
      u = usersLookupMap.byId[String(userOrIdOrName)];
    } else if (typeof userOrIdOrName === 'string') {
      const s = userOrIdOrName.trim().toLowerCase();
      if (s.includes('admin') || s.includes('quản trị')) return 'Quản trị';
      if (s.includes('sale') || s.includes('bán hàng')) return 'Sale';
      if (s.includes('cskh') || s.includes('customer_care') || s.includes('chăm sóc')) return 'CSKH';
      if (s.includes('kế toán') || s.includes('accountant')) return 'Kế toán';
      if (s.includes('kỹ thuật') || s.includes('tech') || s.includes('technician')) return 'Kỹ thuật';
      if (s.includes('lead') || s.includes('marketing')) return 'Lead Marketing';
      if (s.includes('đại lý') || s.includes('agency')) return 'Đại lý';
      if (s.includes('enduser') || s.includes('customer')) return 'Sale';
      u = usersLookupMap.byId[s] || usersLookupMap.byName[s] || usersLookupMap.byUsername[s];
    }
    if (u) {
      const roleId = Number(u.role_id || u.roleId || u.role?.id || 0);
      const rawRole = String(u.role?.title || u.role || u.role_name || '').toLowerCase();
      if (roleId === 1 || rawRole.includes('admin') || rawRole.includes('quản trị')) return 'Quản trị';
      if (roleId === 2 || rawRole.includes('sale') || rawRole.includes('bán hàng')) return 'Sale';
      if (roleId === 3 || rawRole.includes('customer_care') || rawRole.includes('chăm sóc') || rawRole.includes('cskh')) return 'CSKH';
      if (roleId === 4 || rawRole.includes('agency') || rawRole.includes('đại lý')) return 'Đại lý';
      if (roleId === 7 || rawRole.includes('accountant') || rawRole.includes('kế toán')) return 'Kế toán';
      if (roleId === 8 || rawRole.includes('technician') || rawRole.includes('kỹ thuật') || rawRole.includes('tech')) return 'Kỹ thuật';
      if (roleId === 11 || rawRole.includes('lead') || rawRole.includes('marketing')) return 'Lead Marketing';
      if (roleId === 5 || rawRole.includes('enduser') || rawRole.includes('customer')) return 'Sale';
      if (u.role?.title) return u.role.title;
      if (u.role_name && !u.role_name.toLowerCase().includes('enduser')) return u.role_name;
      return roleId ? `Role ${roleId}` : fallback || 'Sale';
    }
    return fallback || '';
  }, [usersLookupMap]);

  const receiptsByOrderId = useMemo(() => {
    const map = {};
    const addReceipt = (r) => {
      if (!r) return;
      const oId = r.order_id || r.order?.id || r.orderId;
      const oCode = r.order_code || r.order?.code || r.orderCode;
      if (oId) {
        const k = String(oId);
        if (!map[k]) map[k] = [];
        if (!map[k].some(x => x.id === r.id || (x.code && x.code === r.code))) {
          map[k].push(r);
        }
      }
      if (oCode) {
        const k = String(oCode).trim().toLowerCase();
        if (!map[k]) map[k] = [];
        if (!map[k].some(x => x.id === r.id || (x.code && x.code === r.code))) {
          map[k].push(r);
        }
      }
    };
    (listReceipts || []).forEach(addReceipt);
    if (Array.isArray(unifiedTransactions)) {
      unifiedTransactions.filter(t => t.type === 'receipt').forEach(addReceipt);
    }
    return map;
  }, [listReceipts, unifiedTransactions]);

  const displayPaginated = useMemo(() => {
    if (!isAnySale && transactionTypeFilter === 'RECEIPTS') {
      const mappedReceipts = (listReceipts || []).map(r => {
        const agId = String(r.payer_id || r.agency_id || r.agency?.id || '');
        const pNameKey = (r.payer_name || r.customer_name || '').trim().toLowerCase();
        const ag = (agId && agenciesMap.byId[agId]) || (pNameKey && agenciesMap.byName[pNameKey]) || null;

        const phone =
          r.phone ||
          r.customer_phone ||
          r.payer_phone ||
          r.agency?.phone ||
          r.agency?.leader?.phone ||
          ag?.leader?.phone ||
          ag?.phone ||
          '';

        const address =
          r.address ||
          r.customer_address ||
          r.company ||
          r.agency?.leader?.address ||
          r.agency?.address ||
          ag?.leader?.address ||
          ag?.address ||
          '';

        const staffName =
          r.creator?.name ||
          r.creator?.full_name ||
          r.creator_name ||
          r.created_by_name ||
          r.created_by?.full_name ||
          r.created_by?.name ||
          r.user?.full_name ||
          r.user?.username ||
          '';

        const matchedUser =
          (r.creator?.id && usersLookupMap.byId[String(r.creator.id)]) ||
          (r.created_by && usersLookupMap.byId[String(r.created_by)]) ||
          (staffName && usersLookupMap.byName[staffName.trim().toLowerCase()]) ||
          r.creator ||
          r.user;

        let extractedRole = '';
        if (staffName.includes('(')) {
          extractedRole = staffName.split('(')[1].replace(')', '').trim();
        }
        const staffRole = getUserRole(matchedUser, extractedRole || r.creator?.role_name || r.creator?.role?.title || (staffName === 'Hệ thống' || !staffName ? 'Hệ thống' : ''));

        const payerName = r.payer_name || r.customer_name || r.agency?.name || ag?.name || '--';

        const rCreatedAt =
          (r.created_at && (String(r.created_at).includes('T') || String(r.created_at).includes(':')) ? r.created_at : null) ||
          (r.createdAt && (String(r.createdAt).includes('T') || String(r.createdAt).includes(':')) ? r.createdAt : null) ||
          r.created_at ||
          r.createdAt ||
          r.receipt_date ||
          r.date;

        const rReceiptDate =
          r.receipt_date ||
          r.date ||
          (rCreatedAt ? moment(rCreatedAt).format('YYYY-MM-DD') : undefined);

        return {
          ...r,
          rowType: 'RECEIPT',
          type: 'receipt',
          amount: r.amount || 0,
          receipt_date: rReceiptDate,
          created_at: rCreatedAt,
          payer_name: payerName,
          customer_name: payerName,
          payer_phone: phone,
          phone,
          customer_phone: phone,
          address,
          customer_address: address,
          account: r.account || r.target_account || r.payment_method_name || 'Tiền mặt',
          note: r.note || '',
          created_by_name: staffName,
          creator_name: staffName,
          created_by_role: staffRole,
          creator: {
            id: r.creator?.id || r.created_by || matchedUser?.id,
            name: staffName,
            full_name: staffName,
            role_name: staffRole,
            role: matchedUser?.role || staffRole
          }
        };
      });

      return [...mappedReceipts].sort((a, b) => {
        const timeA = new Date(a.created_at || a.createdAt || a.receipt_date || a.date || 0).getTime();
        const timeB = new Date(b.created_at || b.createdAt || b.receipt_date || b.date || 0).getTime();
        return timeB - timeA;
      });
    }

    if (!isAnySale && isUnifiedMode && unifiedTransactions) {
      if (!Array.isArray(unifiedTransactions) || unifiedTransactions.length === 0) {
        return [];
      }
      let mapped = unifiedTransactions.map(t => {
        if (t.type === 'receipt') {
          const fullReceipt = receiptsLookupMap.byId[String(t.id)] || receiptsLookupMap.byCode[String(t.code).trim().toLowerCase()];
          const linkedOrderCode = t.order_code || (t.note || '').match(/#?(S\d{6}-\d{3})/i)?.[1];
          const linkedOrderId = t.order_id || fullReceipt?.order_id;
          const linkedOrder = (linkedOrderId && ordersLookupMap.byId[String(linkedOrderId)]) ||
            (linkedOrderCode && ordersLookupMap.byCode[String(linkedOrderCode).trim().toLowerCase()]) ||
            null;

          const agId = String(t.agency?.id || t.agency_id || fullReceipt?.agency_id || fullReceipt?.payer_id || linkedOrder?.agency_id || '');
          const pNameKey = (
            (typeof t.counterparty === 'string' ? t.counterparty : t.counterparty?.name) ||
            fullReceipt?.payer_name ||
            fullReceipt?.customer_name ||
            t.agency?.name ||
            linkedOrder?.agency?.name ||
            linkedOrder?.customer_name ||
            ''
          ).trim().toLowerCase();
          const ag = (agId && agenciesMap.byId[agId]) || (pNameKey && agenciesMap.byName[pNameKey]) || null;

          const rawStaffName = (
            t.creator?.full_name ||
            t.creator?.name ||
            fullReceipt?.creator?.name ||
            fullReceipt?.creator?.full_name ||
            fullReceipt?.creator_name ||
            fullReceipt?.created_by_name ||
            t.creator_name ||
            t.created_by_name ||
            linkedOrder?.sale?.full_name ||
            linkedOrder?.sale?.name ||
            linkedOrder?.creator?.name ||
            ''
          ).trim();

          const staffName = (rawStaffName || 'Hệ thống').split('(')[0].trim();
          const rCreatorId = t.creator?.id || t.created_by || fullReceipt?.creator?.id || fullReceipt?.created_by;

          const matchedUser =
            (rCreatorId && usersLookupMap.byId[String(rCreatorId)]) ||
            (staffName && usersLookupMap.byName[staffName.toLowerCase()]) ||
            (staffName && usersLookupMap.byUsername[staffName.toLowerCase()]) ||
            (dataLogin && (dataLogin.id === rCreatorId || (dataLogin.full_name && dataLogin.full_name.trim().toLowerCase() === staffName.toLowerCase())) ? dataLogin : null) ||
            t.creator ||
            fullReceipt?.creator;

          let extractedRole = '';
          if (rawStaffName.includes('(')) {
            extractedRole = rawStaffName.split('(')[1].replace(')', '').trim();
          }
          const staffRole = getUserRole(
            matchedUser,
            extractedRole || fullReceipt?.creator?.role_name || fullReceipt?.creator?.role?.title || (staffName === 'Hệ thống' || !staffName ? 'Hệ thống' : 'Sale')
          );

          const phone =
            fullReceipt?.phone ||
            fullReceipt?.customer_phone ||
            fullReceipt?.payer_phone ||
            linkedOrder?.customer_phone ||
            linkedOrder?.phone ||
            ag?.leader?.phone ||
            ag?.phone ||
            '';

          const address =
            fullReceipt?.address ||
            fullReceipt?.customer_address ||
            fullReceipt?.company ||
            linkedOrder?.address ||
            linkedOrder?.customer_address ||
            ag?.leader?.address ||
            ag?.address ||
            '';

          const payerName =
            (typeof t.counterparty === 'string' ? t.counterparty : t.counterparty?.name) ||
            fullReceipt?.payer_name ||
            fullReceipt?.customer_name ||
            t.agency?.name ||
            ag?.name ||
            '--';

          const rExactCreatedAt =
            (t.created_at && (String(t.created_at).includes('T') || String(t.created_at).includes(':')) ? t.created_at : null) ||
            (fullReceipt?.created_at && (String(fullReceipt.created_at).includes('T') || String(fullReceipt.created_at).includes(':')) ? fullReceipt.created_at : null) ||
            (t.createdAt && (String(t.createdAt).includes('T') || String(t.createdAt).includes(':')) ? t.createdAt : null) ||
            (fullReceipt?.createdAt && (String(fullReceipt.createdAt).includes('T') || String(fullReceipt.createdAt).includes(':')) ? fullReceipt.createdAt : null) ||
            t.created_at ||
            fullReceipt?.created_at ||
            fullReceipt?.receipt_date ||
            t.date;

          const rExactReceiptDate =
            fullReceipt?.receipt_date ||
            t.receipt_date ||
            t.date ||
            (rExactCreatedAt ? moment(rExactCreatedAt).format('YYYY-MM-DD') : undefined);

          return {
            ...t,
            ...(fullReceipt || {}),
            order_id: linkedOrderId || linkedOrder?.id || null,
            order_code: linkedOrderCode || fullReceipt?.order_code || null,
            rowType: 'RECEIPT',
            type: 'receipt',
            amount: t.amount || fullReceipt?.amount || 0,
            receipt_date: rExactReceiptDate,
            created_at: rExactCreatedAt,
            payer_name: payerName,
            customer_name: payerName,
            payer_phone: phone,
            phone,
            customer_phone: phone,
            address,
            customer_address: address,
            account: t.account || fullReceipt?.account || fullReceipt?.payment_method_name || 'Tiền mặt',
            note: t.note || fullReceipt?.note || '',
            created_by_name: staffName,
            creator_name: staffName,
            created_by_role: staffRole,
            creator: {
              id: rCreatorId || matchedUser?.id,
              name: staffName,
              full_name: staffName,
              role_name: staffRole,
              role: matchedUser?.role || staffRole
            }
          };
        }

        // Order row
        const oIdKey = String(t.order_id || t.id || '');
        const oCodeKey = String(t.code || t.order_code || t.orderCode || '').trim().toLowerCase();
        const fullOrder = (oIdKey && ordersLookupMap.byId[oIdKey]) || (oCodeKey && ordersLookupMap.byCode[oCodeKey]);
        const mergedOrder = { ...(t || {}), ...(fullOrder || {}) };

        let devList = [];
        if (fullOrder?.order_device_types && fullOrder.order_device_types.length > 0) {
          devList = fullOrder.order_device_types.map(d => ({
            ...d,
            device_type_id: d.device_type_id || d.device_type?.id,
            device_type_name:
              d.device_type?.name ||
              d.device_type_name ||
              d.name ||
              d.type ||
              listDeviceType.find(lt => lt.id === (d.device_type_id || d.device_type?.id))?.name ||
              'N/A',
            device_type: d.device_type || { id: d.device_type_id, name: d.device_type_name || d.name },
            serials:
              d.serials ||
              d.order_details?.map(od => od.serial || od.device?.serial || od.device?.imei).filter(Boolean) ||
              [],
            device_ids:
              d.device_ids ||
              d.order_details?.map(od => od.device_id).filter(Boolean) ||
              []
          }));
        } else if (fullOrder?.devices && fullOrder.devices.length > 0) {
          devList = fullOrder.devices.map(d => ({
            ...d,
            device_type_id: d.device_type_id || d.device_type?.id,
            device_type_name:
              d.device_type?.name ||
              d.device_type_name ||
              d.name ||
              d.type ||
              listDeviceType.find(lt => lt.id === (d.device_type_id || d.device_type?.id))?.name ||
              'N/A',
            device_type: d.device_type || { id: d.device_type_id, name: d.device_type_name || d.name },
            serials:
              d.serials ||
              d.order_details?.map(od => od.serial || od.device?.serial || od.device?.imei).filter(Boolean) ||
              [],
            device_ids:
              d.device_ids ||
              d.order_details?.map(od => od.device_id).filter(Boolean) ||
              []
          }));
        } else if (t.order_device_types && t.order_device_types.length > 0) {
          devList = t.order_device_types.map(d => ({
            ...d,
            device_type_id: d.device_type_id || d.device_type?.id,
            device_type_name:
              d.device_type?.name ||
              d.device_type_name ||
              d.name ||
              d.type ||
              listDeviceType.find(lt => lt.id === (d.device_type_id || d.device_type?.id))?.name ||
              'N/A',
            device_type: d.device_type || { id: d.device_type_id, name: d.device_type_name || d.name },
            serials:
              d.serials ||
              d.order_details?.map(od => od.serial || od.device?.serial || od.device?.imei).filter(Boolean) ||
              [],
            device_ids:
              d.device_ids ||
              d.order_details?.map(od => od.device_id).filter(Boolean) ||
              []
          }));
        } else if (t.devices && t.devices.length > 0) {
          devList = t.devices.map(d => ({
            ...d,
            device_type_id: d.device_type_id,
            device_type_name:
              d.name ||
              d.device_type_name ||
              d.device_type?.name ||
              listDeviceType.find(lt => lt.id === d.device_type_id)?.name ||
              'N/A',
            device_type: d.device_type || { id: d.device_type_id, name: d.name }
          }));
        }

        const agId = String(t.agency?.id || t.agency_id || mergedOrder.agency_id || mergedOrder.agency?.id || '');
        const pNameKey = (
          (typeof t.counterparty === 'string' ? t.counterparty : t.counterparty?.name) ||
          t.agency?.name ||
          mergedOrder.customer_name ||
          mergedOrder.agency?.name ||
          ''
        ).trim().toLowerCase();
        const ag = (agId && agenciesMap.byId[agId]) || (pNameKey && agenciesMap.byName[pNameKey]) || null;

        const rawSaleName = (
          t.sale?.full_name ||
          t.sale?.name ||
          mergedOrder.sale?.full_name ||
          mergedOrder.sale?.name ||
          mergedOrder.sale_name ||
          mergedOrder.saleName ||
          mergedOrder.user?.username ||
          mergedOrder.user?.full_name ||
          t.creator?.full_name ||
          t.creator?.name ||
          mergedOrder.creator?.full_name ||
          mergedOrder.creator?.name ||
          mergedOrder.creator_name ||
          mergedOrder.created_by_name ||
          ''
        ).trim();

        const staffName = (rawSaleName || 'Hệ thống').split('(')[0].trim();
        const saleId = t.sale_id || t.sale?.id || mergedOrder.sale_id || mergedOrder.sale?.id;
        const creatorId = t.created_by || t.creator?.id || mergedOrder.created_by || mergedOrder.creator?.id;

        const matchedUser =
          (saleId && usersLookupMap.byId[String(saleId)]) ||
          (creatorId && usersLookupMap.byId[String(creatorId)]) ||
          (staffName && usersLookupMap.byName[staffName.toLowerCase()]) ||
          (staffName && usersLookupMap.byUsername[staffName.toLowerCase()]) ||
          (dataLogin && (dataLogin.id === saleId || dataLogin.id === creatorId || (dataLogin.full_name && dataLogin.full_name.trim().toLowerCase() === staffName.toLowerCase())) ? dataLogin : null) ||
          t.sale ||
          mergedOrder.sale ||
          t.creator ||
          mergedOrder.creator;

        let extractedRole = '';
        if (rawSaleName.includes('(')) {
          extractedRole = rawSaleName.split('(')[1].replace(')', '').trim();
        }
        const staffRole = getUserRole(
          matchedUser,
          extractedRole || mergedOrder.sale?.role_name || mergedOrder.created_by_role || (staffName === 'Hệ thống' || !staffName ? 'Hệ thống' : 'Sale')
        );

        const phone =
          mergedOrder.customer_phone ||
          mergedOrder.phone ||
          mergedOrder.phone_number ||
          mergedOrder.end_user?.phone ||
          ag?.leader?.phone ||
          ag?.phone ||
          '';

        const address =
          mergedOrder.address ||
          mergedOrder.customer_address ||
          mergedOrder.shipping_address ||
          mergedOrder.end_user?.address ||
          ag?.leader?.address ||
          ag?.address ||
          '';

        const custName =
          (typeof t.counterparty === 'string' ? t.counterparty : t.counterparty?.name) ||
          mergedOrder.customer_name ||
          mergedOrder.end_user?.full_name ||
          t.agency?.name ||
          ag?.name ||
          '--';

        const orderPaid = t.paid !== undefined && t.paid !== null
          ? t.paid
          : (mergedOrder.paid !== undefined && mergedOrder.paid !== null ? mergedOrder.paid : mergedOrder.paid_amount);

        return {
          ...mergedOrder,
          id: t.id || fullOrder?.id,
          code: t.code || fullOrder?.code,
          customer_type: t.customer_type || fullOrder?.customer_type || mergedOrder.customer_type,
          paid: orderPaid,
          paid_amount: typeof t.paid_amount === 'number' ? t.paid_amount : (typeof mergedOrder.paid_amount === 'number' ? mergedOrder.paid_amount : orderPaid),
          debt_amount: typeof t.debt_amount === 'number' ? t.debt_amount : (typeof mergedOrder.debt_amount === 'number' ? mergedOrder.debt_amount : 0),
          payment_status: t.payment_status || fullOrder?.payment_status || mergedOrder.payment_status,
          created_at: fullOrder?.created_at || t.created_at || t.date,
          total_amount: t.amount || fullOrder?.total_amount || 0,
          amount: t.amount || fullOrder?.total_amount || 0,
          customer_name: custName,
          customer_phone: phone,
          phone,
          address,
          customer_address: address,
          status: t.status !== undefined ? t.status : (fullOrder?.status !== undefined ? fullOrder.status : 0),
          sale_id: saleId,
          sale_name: staffName !== 'Hệ thống' ? staffName : '',
          saleName: staffName !== 'Hệ thống' ? staffName : '',
          sale: t.sale || mergedOrder.sale || (staffName !== 'Hệ thống' ? {
            id: saleId,
            full_name: staffName,
            name: staffName,
            role_id: matchedUser?.role_id || (staffRole === 'Quản trị' ? 1 : (staffRole === 'Kế toán' ? 3 : (staffRole === 'Kỹ thuật' ? 8 : (staffRole === 'Lead Marketing' ? 11 : 2)))),
            role_name: staffRole,
            role: matchedUser?.role || staffRole
          } : undefined),
          creator_name: t.creator?.full_name || t.creator?.name || mergedOrder.creator_name || mergedOrder.creator?.full_name || mergedOrder.created_by_name || staffName,
          created_by_name: t.creator?.full_name || t.creator?.name || mergedOrder.created_by_name || mergedOrder.creator?.full_name || mergedOrder.creator_name || staffName,
          creator: t.creator || mergedOrder.creator || (creatorId ? {
            id: creatorId,
            name: staffName,
            full_name: staffName,
            role_name: staffRole,
            role: matchedUser?.role || staffRole
          } : undefined),
          order_device_types: devList,
          devices: devList
        };
      });

      if (filterPaymentStatus !== 'all') {
        mapped = mapped.filter(item => item.rowType !== 'RECEIPT' && item.type !== 'receipt');
      }

      if (filterStatus !== 'all') {
        const numStatus = Number(filterStatus);
        mapped = mapped.filter(item => {
          if (item.rowType === 'RECEIPT' || item.type === 'receipt') {
            const ord = (item.order_id && ordersLookupMap.byId[String(item.order_id)]) ||
              (item.order_code && ordersLookupMap.byCode[String(item.order_code).trim().toLowerCase()]);
            return ord ? Number(ord.status) === numStatus : (numStatus === 3);
          }
          return Number(item.status) === numStatus;
        });
      }

      if (filterDevice !== 'all') {
        const numDevice = Number(filterDevice);
        mapped = mapped.filter(item => {
          if (item.rowType === 'RECEIPT' || item.type === 'receipt') {
            const ord = (item.order_id && ordersLookupMap.byId[String(item.order_id)]) ||
              (item.order_code && ordersLookupMap.byCode[String(item.order_code).trim().toLowerCase()]);
            if (!ord) return false;
            return (
              Number(ord.device_type_id) === numDevice ||
              (ord.order_device_types || []).some(odt => Number(odt.device_type_id) === numDevice || Number(odt.device_type?.id) === numDevice) ||
              (ord.devices || []).some(d => Number(d.device_type_id) === numDevice || Number(d.device_type?.id) === numDevice)
            );
          }
          return (
            Number(item.device_type_id) === numDevice ||
            (item.order_device_types || []).some(odt => Number(odt.device_type_id) === numDevice || Number(odt.device_type?.id) === numDevice) ||
            (item.devices || []).some(d => Number(d.device_type_id) === numDevice || Number(d.device_type?.id) === numDevice)
          );
        });
      }

      if (filterSale !== 'all') {
        const targetSaleId = String(filterSale);
        mapped = mapped.filter(item => {
          const itemSaleId = String(item.sale_id || item.sale?.id || '');
          const itemCreatorId = String(item.creator?.id || item.created_by || '');
          if (item.rowType === 'RECEIPT' || item.type === 'receipt') {
            const ord = (item.order_id && ordersLookupMap.byId[String(item.order_id)]) ||
              (item.order_code && ordersLookupMap.byCode[String(item.order_code).trim().toLowerCase()]);
            const ordSaleId = String(ord?.sale_id || ord?.sale?.id || '');
            return (
              itemCreatorId === targetSaleId ||
              itemSaleId === targetSaleId ||
              ordSaleId === targetSaleId
            );
          }
          return itemSaleId === targetSaleId || itemCreatorId === targetSaleId;
        });
      }

      // Sắp xếp các giao dịch: Mới nhất lên đầu
      // Phiếu thu được sắp xếp theo thời điểm lập đơn thu (created_at của phiếu thu), KHÔNG tính theo ngày đơn bán!
      mapped.sort((a, b) => {
        const isReceiptA = a.rowType === 'RECEIPT' || a.type === 'receipt';
        const isReceiptB = b.rowType === 'RECEIPT' || b.type === 'receipt';

        const timeA = new Date(
          isReceiptA
            ? (a.created_at || a.createdAt || a.receipt_date || a.date || 0)
            : (a.created_at || a.createdAt || a.order_date || a.date || 0)
        ).getTime();

        const timeB = new Date(
          isReceiptB
            ? (b.created_at || b.createdAt || b.receipt_date || b.date || 0)
            : (b.created_at || b.createdAt || b.order_date || b.date || 0)
        ).getTime();

        return timeB - timeA;
      });

      return mapped;
    }

    return listOrders || [];
  }, [
    isAnySale,
    transactionTypeFilter,
    isUnifiedMode,
    unifiedTransactions,
    listOrders,
    listReceipts,
    ordersLookupMap,
    receiptsLookupMap,
    agenciesMap,
    usersLookupMap,
    getUserRole,
    filterStatus,
    filterPaymentStatus,
    filterDevice,
    filterSale
  ]);

  const effectiveUnifiedSummary = useMemo(() => {
    if (unifiedSummary && (unifiedSummary.order_amount_sum !== undefined || unifiedSummary.receipt_amount_sum !== undefined)) {
      if (filterPaymentStatus !== 'all') {
        return {
          ...unifiedSummary,
          receipt_amount_sum: 0,
          total_cash_in: 0
        };
      }
      return unifiedSummary;
    }
    let orderSum = 0;
    let paidSum = 0;
    (displayPaginated || []).forEach(item => {
      if (item.rowType === 'RECEIPT' || item.type === 'receipt') {
        if (filterPaymentStatus === 'all') {
          paidSum += Number(item.amount || 0);
        }
      } else {
        orderSum += Number(item.total_amount || item.amount || 0);
        paidSum += Number(item.paid || (item.customer_type === 'AGENCY' ? 0 : item.paid_amount) || 0);
      }
    });
    return {
      order_amount_sum: orderSum,
      receipt_amount_sum: filterPaymentStatus !== 'all' ? 0 : paidSum,
      total_cash_in: filterPaymentStatus !== 'all' ? 0 : paidSum
    };
  }, [unifiedSummary, displayPaginated, filterPaymentStatus]);

  const displayTotal = (!isAnySale && transactionTypeFilter === 'RECEIPTS')
    ? (filterSale !== 'all' ? displayPaginated.length : (totalReceipts || (listReceipts || []).length))
    : (!isAnySale && isUnifiedMode)
      ? (unifiedTotal !== undefined && unifiedTotal !== null ? unifiedTotal : displayPaginated.length)
      : (total || 0);
  const totalPages = Math.ceil((displayTotal || 0) / PAGE_SIZE);
  const paginated = displayPaginated;

  const handleSearch = e => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput ? searchInput.trim() : '');
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);
  const handleStatusChange = e => {
    setFilterStatus(e.target.value);
    setPage(1);
  };
  const handleTypeChange = e => {
    setFilterType(e.target.value);
    setPage(1);
  };

  if (token && (!dataLogin || isGettingInfo)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress style={{ color: '#d32f2f' }} />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} style={{ borderBottom: '1px solid #e0e3ef' }}>
          {isCustomerCare ? (
            <Tabs
              value={mainTab > 1 ? 0 : mainTab}
              onChange={handleMainTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                label="Quản trị lắp đặt"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                label="Quản trị bảo hành"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
            </Tabs>
          ) : isAgency ? (
            <Tabs
              value={mainTab === 3 ? 3 : (mainTab === 1 && canManageReceipts) ? 1 : 0}
              onChange={(e, val) => {
                setMainTab(val);
                const tabMap = { 0: 'sales', 1: 'receipts', 3: 'warranty' };
                navigate(`/app/manage/sales-orders?tab=${tabMap[val] || 'sales'}`, { replace: true });
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                value={0}
                label={isAnySale ? 'Quản trị bán hàng' : 'Quản trị bán hàng & Thu tiền'}
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                value={3}
                label="Quản trị bảo hành"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
            </Tabs>
          ) : isTechnician ? (
            <Tabs
              value={mainTab === 3 ? 3 : 2}
              onChange={(e, val) => {
                setMainTab(val);
                navigate(`/app/manage/sales-orders?tab=${val === 3 ? 'warranty' : 'installation'}`, { replace: true });
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                value={2}
                label="Quản trị lắp đặt"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                value={3}
                label="Quản trị bảo hành"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
            </Tabs>
          ) : (
            <Tabs
              value={mainTab}
              onChange={handleMainTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                value={0}
                label={isAnySale ? 'Quản trị bán hàng' : 'Quản trị bán hàng & Thu tiền'}
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                value={2}
                label="Quản trị lắp đặt"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                value={3}
                label="Quản trị bảo hành"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              <Tab
                value={4}
                label="Quản trị lưu kho"
                style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
              />
              {!isAccountant && (
                <Tab
                  value={5}
                  label="Dashboard & Thống kê"
                  style={{ textTransform: 'none', fontWeight: 700, fontSize: 16 }}
                />
              )}
            </Tabs>
          )}
        </Box>

        {isCustomerCare ? (
          mainTab === 1 ? (
            <WarrantyTicketsView />
          ) : (
            <InstallationsTable />
          )
        ) : mainTab === 0 ? (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={isAnySale ? 'flex-start' : 'space-between'}
              mb={2}
              flexWrap="wrap"
              style={{ gap: 12 }}
            >
              {/* Segmented Filter: Tất cả | Đơn bán hàng | Phiếu thu */}
              {!isAnySale && (
                <Box
                  style={{
                    display: 'inline-flex',
                    backgroundColor: '#f1f5f9',
                    padding: 3,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    position: 'relative'
                  }}
                >
                  {/* Sliding active indicator pill */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: 3,
                      bottom: 3,
                      left: tabIndicatorStyle.left,
                      width: tabIndicatorStyle.width,
                      opacity: tabIndicatorStyle.opacity,
                      backgroundColor: '#ffffff',
                      borderRadius: 6,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
                      transition: 'left 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />

                  <Button
                    ref={el => (tabFilterBtnRefs.current['ALL'] = el)}
                    size="small"
                    onClick={() => {
                      setTransactionTypeFilter('ALL');
                      setPage(1);
                    }}
                    disableRipple
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      fontSize: 13,
                      fontWeight: transactionTypeFilter === 'ALL' ? 700 : 500,
                      backgroundColor: 'transparent',
                      color: transactionTypeFilter === 'ALL' ? '#0f172a' : '#64748b',
                      borderRadius: 6,
                      textTransform: 'none',
                      padding: '4px 14px',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    Tất cả giao dịch
                  </Button>
                  <Button
                    ref={el => (tabFilterBtnRefs.current['ORDERS'] = el)}
                    size="small"
                    onClick={() => {
                      setTransactionTypeFilter('ORDERS');
                      setPage(1);
                    }}
                    disableRipple
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      fontSize: 13,
                      fontWeight: transactionTypeFilter === 'ORDERS' ? 700 : 500,
                      backgroundColor: 'transparent',
                      color: transactionTypeFilter === 'ORDERS' ? '#0f172a' : '#64748b',
                      borderRadius: 6,
                      textTransform: 'none',
                      padding: '4px 14px',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    Đơn bán hàng
                  </Button>
                  {canManageReceipts && (
                    <Button
                      ref={el => (tabFilterBtnRefs.current['RECEIPTS'] = el)}
                      size="small"
                      onClick={() => {
                        setTransactionTypeFilter('RECEIPTS');
                        setPage(1);
                      }}
                      disableRipple
                      style={{
                        position: 'relative',
                        zIndex: 2,
                        fontSize: 13,
                        fontWeight: transactionTypeFilter === 'RECEIPTS' ? 700 : 500,
                        backgroundColor: 'transparent',
                        color: transactionTypeFilter === 'RECEIPTS' ? '#0f172a' : '#64748b',
                        borderRadius: 6,
                        textTransform: 'none',
                        padding: '4px 14px',
                        transition: 'color 0.2s ease'
                      }}
                    >
                      Phiếu thu
                    </Button>
                  )}
                </Box>
              )}

              {/* Status chips (hide when filtering only receipts) */}
              {(isAnySale || transactionTypeFilter !== 'RECEIPTS') && (
                <Box className={classes.summaryRow}>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                    const isActive = filterStatus === Number(key);
                    return (
                      <Box
                        key={key}
                        onClick={() => {
                          setFilterStatus(isActive ? 'all' : Number(key));
                          setPage(1);
                        }}
                        className={classes.summaryChip}
                        style={{
                          borderColor: cfg.color,
                          background: isActive ? cfg.color : cfg.bg,
                          cursor: 'pointer',
                          transform: isActive ? 'translateY(-2px)' : 'none',
                          boxShadow: isActive ? `0 4px 12px ${cfg.color}44` : 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        <span
                          className={classes.summaryDot}
                          style={{ background: isActive ? '#fff' : cfg.color }}
                        />
                        <Typography
                          style={{
                            fontSize: 12,
                            color: isActive ? '#fff' : cfg.color,
                            fontWeight: 700
                          }}
                        >
                          {cfg.label}: {countsByStatus[key] || 0}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            <ToolbarSection
              classes={classes}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              filterPaymentStatus={filterPaymentStatus}
              setFilterPaymentStatus={setFilterPaymentStatus}
              setPage={setPage}
              STATUS_CONFIG={STATUS_CONFIG}
              countsByStatus={countsByStatus}
              searchInput={searchInput}
              handleSearch={handleSearch}
              fromDate={fromDate}
              setFromDate={setFromDate}
              toDate={toDate}
              setToDate={setToDate}
              filterDevice={filterDevice}
              setFilterDevice={setFilterDevice}
              filterSale={filterSale}
              setFilterSale={setFilterSale}
              filterItemType={filterItemType}
              setFilterItemType={setFilterItemType}
              listDeviceType={listDeviceType}
              listSales={listSales}
              search={search}
              setSearch={setSearch}
              setSearchInput={setSearchInput}
              isAdmin={isAdmin}
              isSale={isSale}
              isLeadMarketing={isLeadMarketing}
              isAdminSale={isAdminSale}
              isAgencySale={isAgencySale}
              dataLogin={dataLogin}
              setTabForm={setTabForm}
              setIsEditing={setIsEditing}
              setEditOrderId={setEditOrderId}
              setForm={setForm}
              setDevices={setDevices}
              setOrderParts={setOrderParts}
              setSelectedUser={setSelectedUser}
              setOpenAddModal={setOpenAddModal}
              handleExportExcel={handleExportExcel}
              canManageReceipts={canManageReceipts}
              onOpenAddReceiptGeneral={() => handleOpenAddReceipt()}
              transactionTypeFilter={isAnySale ? 'ORDERS' : transactionTypeFilter}
              setTransactionTypeFilter={setTransactionTypeFilter}
              isAnySale={isAnySale}
            />
            <OrdersTable
              classes={classes}
              paginated={paginated}
              page={page}
              PAGE_SIZE={PAGE_SIZE}
              STATUS_CONFIG={STATUS_CONFIG}
              isScrolled={isScrolled}
              scrollContainerRef={scrollContainerRef}
              onMouseDown={onMouseDown}
              onMouseLeave={onMouseLeave}
              onMouseUp={onMouseUp}
              onMouseMove={onMouseMove}
              handleScroll={handleScroll}
              isDown={isDown}
              isAdmin={isAdmin}
              isSale={isSale}
              isAccountant={isAccountant}
              isLeadMarketing={isLeadMarketing}
              isAgency={isAgency}
              listDeviceType={listDeviceType}
              formatMoney={formatMoney}
              formatInputMoney={formatInputMoney}
              getPaymentStyle={getPaymentStyle}
              localExtraFees={localExtraFees}
              setLocalExtraFees={setLocalExtraFees}
              handleSaveExtraFee={handleSaveExtraFee}
              localTrackingUrls={localTrackingUrls}
              setLocalTrackingUrls={setLocalTrackingUrls}
              handleSaveTrackingUrl={handleSaveTrackingUrl}
              handleDeliveredToggle={handleDeliveredToggle}
              setEditingOrder={setEditingOrder}
              setEditingDevices={setEditingDevices}
              setOrderForImage={setOrderForImage}
              setOpenImageModal={setOpenImageModal}
              setSelectedGallery={setSelectedGallery}
              handleMenuOpen={handleMenuOpen}
              handleCompleteOrder={(order) => {
                setMenuOrder(order);
                setOpenCompleteConfirm(true);
              }}
              total={displayTotal}
              totalAmountSum={(!isAnySale && transactionTypeFilter === 'RECEIPTS') ? receiptTotalAmountSum : totalAmountSum}
              totalPaidSum={totalPaidSum}
              amountBreakdown={(!isAnySale && transactionTypeFilter === 'RECEIPTS') ? null : amountBreakdown}
              transactionTypeFilter={isAnySale ? 'ORDERS' : transactionTypeFilter}
              receiptTotalAmountSum={receiptTotalAmountSum}
              setPage={setPage}
              onViewDetailOrder={handleViewDetailOrder}
              isUnifiedMode={!isAnySale && isUnifiedMode}
              unifiedSummary={effectiveUnifiedSummary}
              onOpenAddReceipt={handleOpenAddReceipt}
              canManageReceipts={canManageReceipts}
              receiptsByOrderId={receiptsByOrderId}
              agencyDebtMap={agencyDebtMap}
              listUsers={listUsers}
              listStaff={listStaff}
              listSales={listSales}
              dataLogin={dataLogin}
              onEditReceipt={(receipt) => {
                setEditingReceipt(receipt);
                setOpenReceiptModal(true);
              }}
              onDeleteReceipt={handleDeleteReceipt}
              onOpenSerialModal={handleOpenSerialModal}
            />
          </>
        ) : (mainTab === 1 && canManageReceipts) ? (
          <PaymentReceiptsTable
            receipts={listReceipts}
            totalReceipts={totalReceipts}
            page={receiptPage}
            setPage={(p) => setReceiptPage(p)}
            pageSize={12}
            totalAmountSum={receiptTotalAmountSum}
            onOpenAddModal={() => handleOpenAddReceipt()}
            onEditReceipt={(item) => handleOpenAddReceipt(item)}
            onDeleteReceipt={handleDeleteReceipt}
            formatMoney={formatMoney}
            isAdmin={isAdmin}
            isAccountant={isAccountant}
            isSale={isSale}
            isLeadMarketing={isLeadMarketing}
            isAgency={isAgency}
            currentAgencyId={currentAgencyId}
            search={receiptSearch}
            setSearch={(val) => {
              setReceiptSearch(val);
              setReceiptPage(1);
            }}
            searchType={receiptSearchType}
            setSearchType={(val) => {
              setReceiptSearchType(val);
              setReceiptPage(1);
            }}
            fromDate={receiptFromDate}
            setFromDate={(val) => {
              setReceiptFromDate(val);
              setReceiptPage(1);
            }}
            toDate={receiptToDate}
            setToDate={(val) => {
              setReceiptToDate(val);
              setReceiptPage(1);
            }}
            accountFilter={receiptAccountFilter}
            setAccountFilter={(val) => {
              setReceiptAccountFilter(val);
              setReceiptPage(1);
            }}
            agencyFilter={receiptAgencyFilter}
            setAgencyFilter={(val) => {
              setReceiptAgencyFilter(val);
              setReceiptPage(1);
            }}
            agencyOrderTotalAmount={agencyOrderTotalAmount}
            agencyOrderCount={agencyOrderCount}
            listAgencies={listAgencies}
            agencyDebtMap={agencyDebtMap}
            listStaff={listStaff}
            listUsers={listUsers}
            listSales={listSales}
            dataLogin={dataLogin}
          />
        ) : mainTab === 2 ? (
          <InstallationsTable />
        ) : mainTab === 3 ? (
          <WarrantyTicketsView />
        ) : mainTab === 4 ? (
          <InventoryTable />
        ) : (
          <SalesDashboard
            listOrders={listOrders}
            listDeviceType={listDeviceType}
            listSales={listSales}
            isAdmin={isAdmin}
            isAccountant={isAccountant}
            isLeadMarketing={isLeadMarketing}
          />
        )}
        <ConfirmDialogs
          classes={classes}
          anchorEl={anchorEl}
          handleMenuClose={handleMenuClose}
          menuOrder={menuOrder}
          isAdmin={isAdmin}
          isSale={isSale}
          isAccountant={isAccountant}
          isLeadMarketing={isLeadMarketing}
          isAdminSale={isAdminSale}
          isAgencySale={isAgencySale}
          dataLogin={dataLogin}
          isAgency={isAgency}
          handleEditOrder={handleEditOrder}
          handleCopyOrder={handleCopyOrder}
          handleDeleteOrder={handleDeleteOrder}
          handleCancelOrder={handleCancelOrder}
          confirmDelete={confirmDelete}
          openCompleteConfirm={openCompleteConfirm}
          setOpenCompleteConfirm={setOpenCompleteConfirm}
          confirmComplete={confirmComplete}
          openCancelConfirm={openCancelConfirm}
          setOpenCancelConfirm={setOpenCancelConfirm}
          confirmCancel={confirmCancel}
          handleViewDetailOrder={handleViewDetailOrder}
          handleBypassSerial={handleBypassSerial}
          onOpenAddReceipt={canManageReceipts ? handleOpenAddReceipt : null}
          openDeleteReceiptConfirm={openDeleteReceiptConfirm}
          setOpenDeleteReceiptConfirm={setOpenDeleteReceiptConfirm}
          confirmDeleteReceipt={confirmDeleteReceipt}
          receiptToDelete={receiptToDelete}
          agencyDebtMap={agencyDebtMap}
          onStatusUpdated={(updatedOrder, newStatus) => {
            if (transactionTypeFilter === 'ALL') {
              fetchOrderReceipts();
            } else {
              fetchListOrders();
            }
            if (updatedOrder && newStatus !== undefined) {
              setAllOrdersCache(prev =>
                (prev || []).map(o => (o.id === updatedOrder.id ? { ...o, status: newStatus } : o))
              );
            }
          }}
        />

        {/* ── Dialog Xem Chi Tiết Đơn Hàng ── */}
        <OrderDetailDialog
          open={openDetailModal}
          onClose={() => {
            setOpenDetailModal(false);
            setViewDetailOrder(null);
          }}
          order={viewDetailOrder}
          STATUS_CONFIG={STATUS_CONFIG}
          listDeviceType={listDeviceType}
          formatMoney={formatMoney}
          getPaymentStyle={getPaymentStyle}
          onEditOrder={handleEditOrder}
          onOpenSerialModal={handleOpenSerialModal}
          setSelectedGallery={setSelectedGallery}
          isAdmin={isAdmin}
          isAccountant={isAccountant}
          isSale={isSale}
          isLeadMarketing={isLeadMarketing}
          isAgency={isAgency}
          canManageReceipts={canManageReceipts}
          receipts={viewDetailOrder ? (receiptsByOrderId[String(viewDetailOrder.id)] || receiptsByOrderId[String(viewDetailOrder.code || '').trim().toLowerCase()] || []) : []}
          agencyDebtMap={agencyDebtMap}
          onOpenAddReceipt={canManageReceipts ? handleOpenAddReceipt : null}
          isAnySale={isAnySale}
        />

        {/* ── Dialog Thêm Đơn Hàng  ── */}
        <OrderDialog
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          isEditing={isEditing}
          editOrderId={editOrderId}
          submitting={submitting}
          isAdmin={isAdmin}
          isAdminSale={isAdminSale}
          isAgencySale={isAgencySale}
          dataLogin={dataLogin}
          form={form}
          setForm={setForm}
          devices={devices}
          setDevices={setDevices}
          orderParts={orderParts}
          setOrderParts={setOrderParts}
          tabForm={tabForm}
          setTabForm={setTabForm}
          fetchType={fetchType}
          setFetchType={setFetchType}
          suggestionLoading={suggestionLoading}
          setSuggestionLoading={setSuggestionLoading}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          setSelectedImages={setSelectedImages}
          handleCreateOrder={handleCreateOrder}
          handleFetchUserInfo={handleFetchUserInfo}
          handleAutocompleteSearch={handleAutocompleteSearch}
          handleAddDevice={handleAddDevice}
          handleRemoveDevice={handleRemoveDevice}
          handleDeviceChange={handleDeviceChange}
          listDeviceType={listDeviceType}
          listAgencies={listAgencies}
          listUsers={listUsers}
          listSales={listSales}
          formatMoney={formatMoney}
          formatInputMoney={formatInputMoney}
          parseInputMoney={parseInputMoney}
        />

        {/* ── Dialog Serial (Scan & Edit) ── */}
        <SerialDialog
          editingOrder={editingOrder}
          setEditingOrder={setEditingOrder}
          newSerial={newSerial}
          setNewSerial={setNewSerial}
          editingDevices={editingDevices}
          setEditingDevices={setEditingDevices}
          isAdmin={isAdmin}
          isAccountant={isAccountant}
          isSale={isSale}
          isLeadMarketing={isLeadMarketing}
          isAgency={isAgency}
          dispatch={dispatch}
          fetchListOrders={fetchListOrders}
          fetchOrderReceipts={fetchOrderReceipts}
          fetchAgencyDebts={fetchAgencyDebts}
          listDeviceType={listDeviceType}
          handleBypassSerial={handleBypassSerial}
        />

        <GalleryModal
          classes={classes}
          openImageModal={openImageModal}
          setOpenImageModal={setOpenImageModal}
          orderForImage={orderForImage}
          setOrderForImage={setOrderForImage}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          selectedGallery={selectedGallery}
          setSelectedGallery={setSelectedGallery}
          deletingImage={deletingImage}
          setDeletingImage={setDeletingImage}
          handleDeleteImage={handleDeleteImage}
          uploadOrderImages={uploadOrderImages}
          updateOrderInList={updateOrderInList}
          getListOrders={getListOrders}
          PAGE_SIZE={PAGE_SIZE}
          page={page}
          isLeadMarketing={isLeadMarketing}
          dispatch={dispatch}
        />

        <PaymentReceiptDialog
          open={openReceiptModal}
          onClose={() => setOpenReceiptModal(false)}
          isEditing={!!editingReceipt?.id}
          receiptData={editingReceipt}
          onSave={handleSaveReceipt}
          listAgencies={listAgencies}
          listUsers={listUsers}
          agencyDebtMap={agencyDebtMap}
          accountOptions={Array.from(new Set(listReceipts.map(r => r.account || r.target_account).filter(Boolean)))}
          formatMoney={formatMoney}
          formatInputMoney={formatInputMoney}
          parseInputMoney={parseInputMoney}
        />
      </div>
      {/* ── Dialog Cập nhật Nợ ── */}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    width: '100%',
    paddingBottom: 4
  },
  inner: {
    width: '97%',
    margin: '0 auto',
    paddingTop: 20,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 76px)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1f36',
    lineHeight: 1.2
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#888',
    marginTop: 2
  },
  summaryRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  summaryChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid'
  },
  summaryDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '12px 16px',
    marginBottom: 12,
    boxShadow: 'none',
    border: '1px solid #e0e3ef'
  },
  searchInput: {
    flex: '1 1 220px',
    minWidth: 200,
    maxWidth: 340,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    }
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 13,
      padding: '10.5px 14px'
    }
  },
  tableCard: {
    boxShadow: 'none',
    border: '1px solid #e0e3ef',
    borderRadius: 8,
    overflow: 'hidden',
    '& ::-webkit-scrollbar': {
      height: '6px'
    },
    '& ::-webkit-scrollbar-track': {
      background: '#f1f1f1'
    },
    '& ::-webkit-scrollbar-thumb': {
      background: '#ccc',
      borderRadius: '3px'
    },
    '& ::-webkit-scrollbar-thumb:hover': {
      background: '#3f51b5'
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f9faff !important'
    }
  },
  customerName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a1f36',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: '1.4em',
    wordBreak: 'break-word'
  },
  typeChip: {
    fontWeight: 600,
    fontSize: 11,
    height: 22,
    borderRadius: 4
  },
  trackingLink: {
    fontSize: 12,
    color: '#3f51b5',
    textDecoration: 'underline',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0
  },
  paginationRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingRight: 4,
    flexWrap: 'wrap',
    gap: 8
  },
  stickyCol: {
    position: 'sticky',
    left: 0,
    backgroundColor: '#ffffff',
    zIndex: 10
  },
  stickyHeader: {
    position: 'sticky',
    left: 0,
    zIndex: 11,
    backgroundColor: '#f5f6fa !important'
  },
  receiptRow: {
    backgroundColor: '#e6f7f5 !important',
    '& td': {
      backgroundColor: '#e6f7f5 !important'
    },
    '&:hover': {
      backgroundColor: '#d1f2eb !important',
      '& td': {
        backgroundColor: '#d1f2eb !important'
      }
    }
  }
}));

export default SalesOrdersView;
